import React, { useState, useEffect, useRef } from 'react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';
import AvisValeurCarte, { StatistiquesAvis } from '../components/AvisValeurCarte';

const API = '/api/estimation';
const CADASTRE_API = '/api/cadastre';
function getToken() { return localStorage.getItem('authToken') || ''; }
function headers() { return { Authorization: `Bearer ${getToken()}` }; }
function jsonHeaders() { return { ...headers(), 'Content-Type': 'application/json' }; }

// ── Libellés ──────────────────────────────────────────────────────────────
const MOTIF_LABELS: Record<string, string> = {
  vente: 'Projet de Vente',
  patrimoniale: 'Estimation Patrimoniale',
  fiscale: 'Estimation Fiscale',
  achat: "Projet d'Achat",
  successorale: 'Estimation successorale',
  simple: 'Estimation simple',
};
const MOTIF_ORDER = ['vente', 'patrimoniale', 'fiscale', 'achat', 'successorale', 'simple'];

const TYPE_BIEN_LABELS: Record<string, string> = {
  Maison: 'Maison',
  Appartement: 'Appartement',
  Terrain: 'Terrain',
  Ferme: 'Ferme / Propriété rurale',
  Commerce: 'Commerce / Local commercial',
  Immeuble: 'Immeuble',
  Autre: 'Autre',
};
const TYPE_BIEN_ORDER = ['Maison', 'Appartement', 'Terrain', 'Ferme', 'Commerce', 'Immeuble', 'Autre'];
// Un terrain nu n'a ni étage, ni exposition, ni DPE, ni travaux intérieurs à renseigner.
const TYPES_SANS_CRITERES_BATI = new Set(['Terrain']);

const ETAT_LABELS: Record<string, string> = {
  a_renover: 'À rénover',
  bon_etat: 'Bon état',
  tres_bon_etat: 'Très bon état',
  neuf: 'Neuf',
};
const ETAGE_LABELS: Record<string, string> = {
  rdc: 'Rez-de-chaussée',
  intermediaire: 'Étage intermédiaire',
  dernier_sans_asc: 'Dernier étage (sans ascenseur)',
  dernier_avec_asc: 'Dernier étage (avec ascenseur)',
};
const EXPOSITION_LABELS: Record<string, string> = {
  nord: 'Nord',
  est_ouest: 'Est / Ouest',
  sud: 'Sud',
};
const DPE_LETTRES = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
const DPE_VIERGE = 'X'; // bien non soumis au DPE (terrain, certains locaux...) — distinct de "non renseigné"
const STATUT_LABELS: Record<string, string> = { brouillon: 'Brouillon', finalise: 'Finalisé' };

const ETAT_AVANCEMENT_ORDER = ['a_contacter', 'rendez_vous', 'envoye', 'signe_mandat', 'vendu', 'perdu'];
const ETAT_AVANCEMENT_LABELS: Record<string, string> = {
  a_contacter: 'À contacter',
  rendez_vous: 'Rendez-vous',
  envoye: 'Rapport envoyé',
  signe_mandat: 'Mandat signé',
  vendu: 'Vendu',
  perdu: 'Perdu',
};
const ETAT_AVANCEMENT_COLORS: Record<string, string> = {
  a_contacter: 'bg-gray-50 text-gray-600 border-gray-200',
  rendez_vous: 'bg-blue-50 text-blue-700 border-blue-200',
  envoye: 'bg-violet-50 text-violet-700 border-violet-200',
  signe_mandat: 'bg-amber-50 text-amber-700 border-amber-200',
  vendu: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  perdu: 'bg-red-50 text-red-700 border-red-200',
};

const AJUSTEMENT_ORDER = ['decoteOccupe', 'autresElements', 'travauxARealiser', 'valorisationTerrain', 'impactEnergetique'];
const AJUSTEMENT_LABELS: Record<string, string> = {
  decoteOccupe: 'Décote si bien vendu occupé',
  autresElements: "Autres éléments d'ajustement",
  travauxARealiser: 'Travaux à réaliser',
  valorisationTerrain: 'Valorisation du terrain',
  impactEnergetique: "Impact de la performance énergétique",
};

const ACTIVITE_ICONS: Record<string, string> = {
  creation: '📁',
  etat_avancement: '🔄',
  pdf_genere: '📄',
  note: '📝',
  modification: '✏️',
};

// Critères qualitatifs regroupés par catégorie (façon "Caractéristiques" chez
// la concurrence) — chaque critère est noté 1 à 5 par l'agent (index de
// l'option choisie). Sert au radar de profil du bien et à un léger ajustement
// de prix (indice de qualité, calculé côté serveur).
interface CritereDef { key: string; label: string; options: string[]; }
interface CategorieDef { key: string; label: string; criteres: CritereDef[]; }

const CRITERES_CATEGORIES: CategorieDef[] = [
  { key: 'elements_principaux', label: 'Éléments principaux', criteres: [
    { key: 'etat_gros_oeuvre', label: 'État et qualité du gros œuvre', options: ['Mauvais', 'Médiocre', 'Moyen', 'Bon', 'Très bon'] },
    { key: 'epoque_construction', label: 'Époque de construction', options: ['Pas recherchée', 'Peu recherchée', 'Neutre', 'Recherchée', 'Très recherchée'] },
    { key: 'mitoyennete', label: 'Mitoyenneté', options: ['3 côtés', '2 côtés', '1 côté', 'Par dépendance', 'Indépendante'] },
    { key: 'agrement_general', label: 'Agrément général', options: ['Très désagréable', 'Désagréable', 'Moyen', 'Agréable', 'Très agréable'] },
    { key: 'standing', label: 'Standing', options: ['Mauvais', 'Médiocre', 'Moyen', 'Bon', 'Très bon'] },
  ] },
  { key: 'environnement_qual', label: 'Environnement', criteres: [
    { key: 'calme', label: 'Calme du quartier', options: ['Très bruyant', 'Bruyant', 'Moyen', 'Calme', 'Très calme'] },
    { key: 'vue', label: 'Vue', options: ['Aucune', 'Limitée', 'Correcte', 'Belle', 'Exceptionnelle'] },
    { key: 'nuisances', label: 'Nuisances (bruit, pollution)', options: ['Fortes', 'Notables', 'Modérées', 'Faibles', 'Aucune'] },
  ] },
  { key: 'sejour', label: 'Le séjour', criteres: [
    { key: 'luminosite_sejour', label: 'Luminosité', options: ['Très sombre', 'Sombre', 'Moyenne', 'Lumineux', 'Très lumineux'] },
    { key: 'volume_sejour', label: 'Surface / volume', options: ['Très petit', 'Petit', 'Moyen', 'Grand', 'Très grand'] },
  ] },
  { key: 'cuisine', label: 'La cuisine', criteres: [
    { key: 'equipement_cuisine', label: 'Équipement', options: ['Absent', 'Sommaire', 'Standard', 'Bien équipée', 'Haut de gamme'] },
    { key: 'agencement_cuisine', label: 'Agencement', options: ['Mauvais', 'Médiocre', 'Moyen', 'Bon', 'Très bon'] },
  ] },
  { key: 'chambres', label: 'Les chambres', criteres: [
    { key: 'nombre_chambres', label: 'Nombre suffisant', options: ['Insuffisant', 'Limite', 'Correct', 'Bon', 'Très bon'] },
    { key: 'surface_chambres', label: 'Surface', options: ['Très petites', 'Petites', 'Moyennes', 'Grandes', 'Très grandes'] },
  ] },
  { key: 'sanitaires', label: 'Les sanitaires', criteres: [
    { key: 'nombre_sanitaires', label: 'Nombre', options: ['Insuffisant', 'Limite', 'Correct', 'Bon', 'Très bon'] },
    { key: 'etat_sanitaires', label: 'État', options: ['Mauvais', 'Médiocre', 'Moyen', 'Bon', 'Très bon'] },
  ] },
  { key: 'energie', label: 'Énergie', criteres: [
    { key: 'isolation', label: 'Isolation', options: ['Mauvaise', 'Médiocre', 'Moyenne', 'Bonne', 'Très bonne'] },
    { key: 'chauffage', label: 'Chauffage', options: ['Vétuste', 'Ancien', 'Standard', 'Récent', 'Très performant'] },
  ] },
  { key: 'annexes', label: 'Les annexes', criteres: [
    { key: 'stationnement', label: 'Garage / stationnement', options: ['Aucun', 'Rue seulement', 'Place ext.', 'Garage simple', 'Garage + ext.'] },
    { key: 'exterieur', label: 'Extérieur (jardin/terrasse)', options: ['Aucun', 'Minime', 'Correct', 'Agréable', 'Exceptionnel'] },
  ] },
  { key: 'criteres_generaux', label: 'Critères généraux', criteres: [
    { key: 'luminosite_generale', label: 'Luminosité générale', options: ['Très sombre', 'Sombre', 'Moyenne', 'Lumineux', 'Très lumineux'] },
    { key: 'accessibilite', label: 'Accessibilité / stationnement', options: ['Difficile', 'Limitée', 'Correcte', 'Facile', 'Très facile'] },
  ] },
];

function formatEUR(n: number | null | undefined): string {
  if (n == null) return '—';
  return n.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + ' €';
}
function formatDateFr(d: string | null | undefined): string {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('fr-FR'); } catch { return d; }
}
function formatDateHeureFr(d: string | null | undefined): string {
  if (!d) return '—';
  try {
    const date = new Date(d);
    return `${date.toLocaleDateString('fr-FR')} à ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
  } catch { return d; }
}
function isEmailValide(email: string): boolean {
  if (!email.trim()) return true; // champ optionnel — vide = valide
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}
function formatTrajetCourt(e?: { distanceVolOiseauKm?: number | null; distanceRouteKm?: number | null; dureeVoitureMin?: number | null } | null): string {
  if (!e) return '—';
  if (e.dureeVoitureMin != null) return `${e.dureeVoitureMin} min${e.distanceRouteKm != null ? ` (${e.distanceRouteKm} km)` : ''}`;
  if (e.distanceVolOiseauKm != null) return `${e.distanceVolOiseauKm} km à vol d'oiseau`;
  return '—';
}

// ── Types ─────────────────────────────────────────────────────────────────
interface Suggestion {
  label: string;
  city: string;
  postcode: string;
  citycode: string;
  type: string;
  lon: number;
  lat: number;
}

interface Comparable {
  idMutation: string;
  dateVente: string;
  annee: number;
  adresse: string;
  surface: number;
  prix: number;
  prixM2: number;
  distanceM: number;
  lon: number;
  lat: number;
}

interface Resultat {
  prixM2Base: number | null;
  coefficientAjustement: number | null;
  prixBas: number | null;
  prixHaut: number | null;
  prixRetenu: number | null;
  prixCapitalisation: number | null;
  indiceQualitePct: number | null;
  montantFinal: number | null;
}

interface Activite {
  id: number;
  type: string;
  description: string;
  created_at: string;
}

interface TrajetInfo { nom?: string; commune?: string | null; numero?: string | null; distanceVolOiseauKm?: number | null; distanceRouteKm?: number | null; dureeVoitureMin?: number | null; }
interface PoiItem { nom: string; type: string | null; distanceM: number; }
interface Environnement {
  poi: { administration: PoiItem[]; sante: PoiItem[]; transports: PoiItem[]; status?: string };
  ecoles: { status: string; maternelle?: TrajetInfo | null; primaire?: TrajetInfo | null; college?: TrajetInfo | null; lycee?: TrajetInfo | null };
  gare: { status: string; gare?: TrajetInfo | null };
  autoroute: { sortie?: TrajetInfo | null };
}

interface ParcelleInfo {
  idu: string;
  section: string;
  numero: string;
  nomCom: string;
  contenanceM2: number;
  geometry: { type: string; coordinates: any } | null;
}
interface ZonageInfo {
  libelle: string;
  libelong: string;
  typezone: string;
  ambigu?: boolean;
  zoneAlternative?: { libelle: string } | null;
}
interface BatimentInfo {
  nombreEtages: number | null;
  nombreLogements: number | null;
  materiauxMurs: string | null;
  materiauxToiture: string | null;
  empriseSolM2: number | null;
  anneeConstruction: number | null;
}
interface EligibiliteInternet {
  status: 'ready' | 'importing' | 'unavailable';
  technologies?: Array<{
    technologie: string | null;
    debitDescendant: string | null;
    debitMontant: string | null;
    operateur: string | null;
  }> | null;
  distanceM?: number | null;
}
interface LogementCommune {
  status: 'ready' | 'importing' | 'unavailable';
  donnees?: {
    maisons: number; appartements: number;
    pieces_1: number; pieces_2: number; pieces_3: number; pieces_4: number; pieces_5p: number;
    proprietaires: number; locataires_non_hlm: number; locataires_hlm: number; loge_gratuit: number;
    total_residences_principales: number;
  } | null;
}
interface ConstructionsNeuves {
  status: 'ready' | 'importing' | 'unavailable';
  donnees?: {
    log_ind_commences: number; log_col_commences: number;
    log_ind_autorises: number; log_col_autorises: number;
  } | null;
  anneesCouvertes?: number[];
}
interface TauxOAT {
  status: 'ready' | 'importing' | 'unavailable';
  serie10?: Array<{ periode: string; valeur: number }>;
  serie5?: Array<{ periode: string; valeur: number }>;
}
interface Urbanisme {
  parcelle: ParcelleInfo | null;
  zonage: ZonageInfo | null;
  batiments: BatimentInfo[];
  eligibiliteInternet: EligibiliteInternet | null;
  logementCommune: LogementCommune | null;
  constructionsNeuves: ConstructionsNeuves | null;
  tauxOAT: TauxOAT | null;
}

// Codes techno Arcep ("Ma connexion internet") — mêmes libellés que la fiche
// Cadastre, pour rester cohérent entre les deux modules.
const TECHNO_LABELS: Record<string, string> = {
  FO: 'Fibre optique (FTTH)',
  CU: 'Cuivre (ADSL/VDSL)',
  CA: 'Câble',
  '4GF': '4G fixe',
  SAT: 'Satellite',
  THD_RADIO: 'THD radio',
};
const TECHNO_ICONS: Record<string, string> = {
  FO: '🔌',
  CU: '☎️',
  CA: '📺',
  '4GF': '📡',
  SAT: '🛰️',
  THD_RADIO: '📻',
};

// "code_operateur" Arcep ("Ma connexion internet") — codes à 4 lettres
// (ex: BOUY, FREE, NORN, SFR0...) qui ne sont PAS des abréviations mais de
// vrais codes internes Arcep. Décodés ici à partir du référentiel officiel
// operateur.csv (data.gouv.fr / data.arcep.fr) pour ne jamais afficher un
// code brut à l'agent ou au client. Liste volontairement large (opérateurs
// nationaux + réseaux d'initiative publique régionaux) puisqu'un immeuble
// rural est souvent raccordé via un réseau local plutôt qu'un grand opérateur.
const OPERATEUR_LABELS: Record<string, string> = {
  FRTE: 'Orange', SRR: 'SFR', SFR0: 'SFR', UPCF: 'SFR Fibre', BOUY: 'Bouygues Telecom', FREE: 'Free',
  STAK: 'Starlink', MILK: 'MilkyWan', NUAT: 'Numerisat', NORN: 'Nordnet', AXIO: 'Axione', CPOD: 'Altitude Infrastructure',
  KWAO: 'K-Net', CORI: 'Coriolis Telecom', COMC: 'Comcable', TDFF: 'TDF Fibre', VIAL: 'Vialis', VITS: 'VITIS',
  GUYA: 'Guyacom', DAUF: 'Dauphin Telecom', MNUM: 'Manche Numérique', GEFI: 'Gefi', ZEOP: 'Zeop',
  AF21: 'Altitude Fibre 21', AF40: 'Altitude Fibre 40', JURA: 'Altitude Fibre 39', AISN: 'AISNETHD',
  AXTD: 'Alliance Très Haut Débit', ANFI: 'Anjou Fibre', ARTD: 'Ariège Très Haut Débit', ATHD: 'Auvergne THD',
  AXLI: 'Axione Limousin', BEFO: 'Berry Fibre Optique', BFCF: 'BFC Fibre', CAPS: 'CAPS Très Haut Débit',
  CMTD: 'Charente-Maritime Très Haut Débit', CO92: 'Covage 92', COAG: 'Covage THD', DETE: 'Debitex Telecom',
  SY79: 'Deux-Sèvres Numérique', MENI: 'Doubs THD', BART: 'Dunkerque Grand Littoral Networks',
  ENTH: 'Essonne Numérique THD', EULH: 'Eure-et-Loir THD', GERS: 'Gers Numérique', GTHD: 'Gironde Très Haut Débit',
  GDHD: 'Grand Dax Très Haut Débit', INOL: 'Inolia', ISER: 'Isère THD', AQTH: 'La Fibre Nouvelle-Aquitaine',
  LTHD: 'Laval Très Haut Débit', LOSA: 'Losange', LOIR: 'Lysseo (Loiret THD)', MAFI: 'Manche Fibre',
  MANC: 'Manche Telecom', MART: 'Martinique THD', MAYE: 'Mayenne Fibre', MONU: 'Moselle Numérique',
  MOSL: 'Moselle Telecom', NIVE: 'Nivertel', ONUM: 'Oise Numérique', OPTE: 'Opalys Telecom',
  R135: 'Ozone', PACT: 'Provence Alpes ConnecT', NETP: 'RESOPTIC', ROSA: 'Rosace',
  SETR: 'Seine Essonne Très Haut Débit', SMTH: 'Seine-et-Marne THD', SETE: 'Sequantic Telecom',
  SPTH: 'Société Palloise du Très Haut Débit', SOGA: 'Solstice Grand Angoulême', VOFI: "Val d'Oise Fibre",
  VDLF: 'Val-de-Loire Fibre', VALO: 'Valofibre', VANN: 'Vannes Agglo Numérique', VANU: 'Vaucluse Numérique',
  VENU: 'Vendée Numérique', YVFI: 'Yvelines Fibre', WIGA: 'Wigard', EMER: 'Emeraude THD', LRCA: 'La Rochelle THD',
  FI31: 'La Fibre 31', HD66: 'THD66', VTHD: 'Var Très Haut Débit', VIEN: 'Vienne Numérique', CORF: 'Corsica Fibra',
  THDD: 'THD64', OCFI: 'Octogone Fibre', TARN: 'Tarn Fibre', RRTH: 'Régie Réunion THD', EURE: 'Eure Normandie THD',
  GUDI: 'Guadeloupe Digital', KOUR: 'Kourou Fibre', LOFI: 'Loiret Fibre', HASF: 'Haute Saône Fibre',
  BTHD: 'Bretagne Très Haut Débit', FI44: 'Fibre44', THDT: 'THDtel', SHSN: 'Haute-Saône Numérique',
  DP71: 'Département de Saône-et-Loire', SIEA: "Syndicat d'énergie et de e-communication de l'Ain",
};
function labelOperateur(code: string | null | undefined): string {
  if (!code) return 'Opérateur';
  return OPERATEUR_LABELS[code.trim().toUpperCase()] || code;
}
const DEBIT_CLASSE_LABELS: Record<string, string> = {
  INEL: 'Inéligible',
  RIP: 'Inéligible',
  BD: '3 à 8 Mb/s',
  BD8: '8 à 30 Mb/s',
  THD30: '30 à 100 Mb/s',
  THD100: '100 Mb/s à 1 Gb/s',
  THD1G: '1 Gb/s et plus',
};
function formatDebit(classe: string | null | undefined): string | null {
  if (!classe) return null;
  if (DEBIT_CLASSE_LABELS[classe]) return DEBIT_CLASSE_LABELS[classe];
  const m = /^THD(\d+)(G)?$/i.exec(classe);
  if (m) return `${m[1]} ${m[2] ? 'Gb/s' : 'Mb/s'} et plus`;
  return null;
}

// Codes MAJIC "dmatgm"/"dmatto" (matériaux des murs/toiture, fichiers
// fonciers DGFiP) — nomenclature officielle documentée par le Cerema
// (doc-datafoncier.cerema.fr), reprise ici pour ne jamais afficher un code
// brut ("90", "10"...) à l'agent ou au client.
const MATERIAUX_MURS_LABELS: Record<string, string> = {
  '0': 'Indéterminé', '1': 'Pierre', '2': 'Meulière', '3': 'Béton', '4': 'Briques', '5': 'Aggloméré', '6': 'Bois', '9': 'Autres',
  '10': 'Pierre', '11': 'Pierre', '12': 'Meulière - Pierre', '13': 'Béton - Pierre', '14': 'Briques - Pierre', '15': 'Aggloméré - Pierre', '16': 'Bois - Pierre', '19': 'Pierre - Autres',
  '20': 'Meulière', '21': 'Meulière - Pierre', '22': 'Meulière', '23': 'Béton - Meulière', '24': 'Briques - Meulière', '25': 'Aggloméré - Meulière', '26': 'Bois - Meulière', '29': 'Meulière - Autres',
  '30': 'Béton', '31': 'Béton - Pierre', '32': 'Béton - Meulière', '33': 'Béton', '34': 'Béton - Briques', '35': 'Aggloméré - Béton', '36': 'Béton - Bois', '39': 'Béton - Autres',
  '40': 'Briques', '41': 'Briques - Pierre', '42': 'Briques - Meulière', '43': 'Béton - Briques', '44': 'Briques', '45': 'Aggloméré - Briques', '46': 'Bois - Briques', '49': 'Briques - Autres',
  '50': 'Aggloméré', '51': 'Aggloméré - Pierre', '52': 'Aggloméré - Meulière', '53': 'Aggloméré - Béton', '54': 'Aggloméré - Briques', '55': 'Aggloméré', '56': 'Aggloméré - Bois', '59': 'Aggloméré - Autres',
  '60': 'Bois', '61': 'Bois - Pierre', '62': 'Bois - Meulière', '63': 'Béton - Bois', '64': 'Bois - Briques', '65': 'Aggloméré - Bois', '66': 'Bois', '69': 'Bois - Autres',
  '90': 'Autres', '91': 'Pierre - Autres', '92': 'Meulière - Autres', '93': 'Béton - Autres', '94': 'Briques - Autres', '95': 'Aggloméré - Autres', '96': 'Bois - Autres', '99': 'Autres',
};
const MATERIAUX_TOITURE_LABELS: Record<string, string> = {
  '0': 'Indéterminé', '1': 'Tuiles', '2': 'Ardoises', '3': 'Zinc / aluminium', '4': 'Béton', '9': 'Autres',
  '10': 'Tuiles', '11': 'Tuiles', '12': 'Ardoises - Tuiles', '13': 'Tuiles - Zinc/alu', '14': 'Béton - Tuiles', '19': 'Tuiles - Autres',
  '20': 'Ardoises', '21': 'Ardoises - Tuiles', '22': 'Ardoises', '23': 'Ardoises - Zinc/alu', '24': 'Ardoises - Béton', '29': 'Ardoises - Autres',
  '30': 'Zinc / aluminium', '31': 'Tuiles - Zinc/alu', '32': 'Ardoises - Zinc/alu', '33': 'Zinc / aluminium', '34': 'Béton - Zinc/alu', '39': 'Zinc/alu - Autres',
  '40': 'Béton', '41': 'Béton - Tuiles', '42': 'Ardoises - Béton', '43': 'Béton - Zinc/alu', '44': 'Béton', '49': 'Béton - Autres',
  '90': 'Autres', '91': 'Tuiles - Autres', '92': 'Ardoises - Autres', '93': 'Zinc/alu - Autres', '94': 'Béton - Autres', '99': 'Autres',
};
function formatMateriauMurs(code: string | null | undefined): string | null {
  if (!code) return null;
  return MATERIAUX_MURS_LABELS[code.trim()] || null;
}
function formatMateriauToiture(code: string | null | undefined): string | null {
  if (!code) return null;
  return MATERIAUX_TOITURE_LABELS[code.trim()] || null;
}

interface Dossier {
  id: number;
  client_nom: string | null;
  client_email: string | null;
  motif: string;
  adresse_label: string;
  code_insee: string | null;
  code_dep: string | null;
  lon: number;
  lat: number;
  type_bien: string;
  surface_habitable: number;
  nb_pieces: number | null;
  annee_construction: number | null;
  analyse_ia: string | null;
  etat_general: string;
  etage: string | null;
  exposition: string | null;
  travaux_recents: boolean;
  dpe_lettre: string | null;
  comparables_retenus: Comparable[];
  prix_m2_base: number | null;
  coefficient_ajustement: number | null;
  prix_bas: number | null;
  prix_haut: number | null;
  prix_retenu: number | null;
  statut: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
  demandeur_telephone: string | null;
  demandeur_adresse: string | null;
  proprietaire_est_demandeur: boolean;
  proprietaire_nom: string | null;
  proprietaire_telephone: string | null;
  proprietaire_email: string | null;
  etat_avancement: string;
  criteres_qualitatifs: Record<string, Record<string, number>> | null;
  indice_qualite_pct: number | null;
  ajustements_prix: Record<string, { mode: string; valeur: number }> | null;
  methode_comparaison_active: boolean;
  methode_capitalisation_active: boolean;
  loyer_mensuel_estime: number | null;
  taux_rendement_pct: number | null;
  prix_capitalisation: number | null;
  honoraires_pct: number | null;
  affichage_net_vendeur: boolean;
  montant_final: number | null;
}

interface AgentProfile {
  name: string;
  activite: string | null;
  entreprise: string | null;
  ville: string | null;
  telephone: string | null;
  siteWeb: string | null;
  email: string | null;
  photoUrl: string | null;
}

interface AjustementLigne { mode: 'euro' | 'pct'; valeur: string; }

interface Draft {
  clientNom: string;
  clientEmail: string;
  motif: string;
  adresseLabel: string;
  codeInsee: string | null;
  codeDep: string | null;
  lon: number | null;
  lat: number | null;
  typeBien: string;
  surfaceHabitable: string;
  nbPieces: string;
  anneeConstruction: string;
  etatGeneral: string;
  etage: string;
  exposition: string;
  travauxRecents: boolean;
  dpeLettre: string;
  notes: string;
  demandeurTelephone: string;
  demandeurAdresse: string;
  proprietaireEstDemandeur: boolean;
  proprietaireNom: string;
  proprietaireTelephone: string;
  proprietaireEmail: string;
  etatAvancement: string;
  criteresQualitatifs: Record<string, Record<string, number>>;
  ajustements: Record<string, AjustementLigne>;
  methodeComparaisonActive: boolean;
  methodeCapitalisationActive: boolean;
  loyerMensuelEstime: string;
  tauxRendementPct: string;
  honorairesPct: string;
  affichageNetVendeur: boolean;
}

const BLANK_AJUSTEMENTS: Record<string, AjustementLigne> = Object.fromEntries(
  AJUSTEMENT_ORDER.map(k => [k, { mode: 'euro' as const, valeur: '' }])
);

const BLANK_DRAFT: Draft = {
  clientNom: '',
  clientEmail: '',
  motif: 'vente',
  adresseLabel: '',
  codeInsee: null,
  codeDep: null,
  lon: null,
  lat: null,
  typeBien: 'Maison',
  surfaceHabitable: '',
  nbPieces: '',
  anneeConstruction: '',
  etatGeneral: 'bon_etat',
  etage: '',
  exposition: '',
  travauxRecents: false,
  dpeLettre: '',
  notes: '',
  demandeurTelephone: '',
  demandeurAdresse: '',
  proprietaireEstDemandeur: true,
  proprietaireNom: '',
  proprietaireTelephone: '',
  proprietaireEmail: '',
  etatAvancement: 'a_contacter',
  criteresQualitatifs: {},
  ajustements: BLANK_AJUSTEMENTS,
  methodeComparaisonActive: true,
  methodeCapitalisationActive: false,
  loyerMensuelEstime: '',
  tauxRendementPct: '5',
  honorairesPct: '5',
  affichageNetVendeur: true,
};

function buildAjustementsPrix(ajustements: Record<string, AjustementLigne>): Record<string, { mode: string; valeur: number }> {
  const result: Record<string, { mode: string; valeur: number }> = {};
  for (const [k, v] of Object.entries(ajustements)) {
    const n = parseFloat(v.valeur);
    if (Number.isFinite(n) && n !== 0) result[k] = { mode: v.mode, valeur: n };
  }
  return result;
}

function computeRadarData(criteresQualitatifs: Record<string, Record<string, number>>) {
  return CRITERES_CATEGORIES.map(cat => {
    const vals = cat.criteres
      .map(c => criteresQualitatifs[cat.key]?.[c.key])
      .filter((v): v is number => v != null);
    const score = vals.length ? Math.round(((vals.reduce((a, b) => a + b, 0) / vals.length - 1) / 4) * 100) : 60;
    return { categorie: cat.label, score, note: vals.length > 0 };
  });
}

// Ligne de sélection 1-5 pour un critère qualitatif (façon segmented control).
function CritereRow({ label, options, value, onChange }: { label: string; options: string[]; value: number | undefined; onChange: (v: number) => void }) {
  return (
    <div className="mb-3">
      <p className="text-xs font-medium text-gray-600 mb-1">{label}</p>
      <div className="grid grid-cols-5 gap-1">
        {options.map((opt, i) => {
          const v = i + 1;
          const active = value === v;
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(v)}
              className={`text-[10px] leading-tight px-1 py-1.5 rounded-lg border text-center transition-colors ${active ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Couleur de marque SAFTI — utilisée uniquement dans le rapport client
// (printRef / PDF exporté). L'interface de travail (formulaire, onglets)
// conserve le violet propre à l'outil.
const PRINT_BRAND = '#F27D49';
const PRINT_SAT_SIZE = 1000;

const COVER_TITLES: Record<string, string> = {
  vente: 'ÉTUDE DE MARCHÉ',
  patrimoniale: 'ESTIMATION PATRIMONIALE',
  fiscale: 'ESTIMATION FISCALE',
  achat: "ÉTUDE D'ACQUISITION",
  successorale: 'ESTIMATION SUCCESSORALE',
  simple: 'ESTIMATION IMMOBILIÈRE',
};

// Projection équirectangulaire + bbox WMS pour la vue satellite du rapport —
// même logique que ParcelleSatellite dans CadastrePage.tsx (bbox en
// lat,lon requis par WMS 1.3.0 + EPSG:4326). Fallback sur un petit carré
// centré sur le point si aucune géométrie de parcelle n'est disponible
// (dossier pas encore rattaché à un cadastre précis).
function computeSatelliteView(
  geometry: { type: string; coordinates: any } | null | undefined,
  lon: number,
  lat: number
): { bbox: string; pathD: string; size: number } | null {
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;

  const cosLat0 = Math.cos((lat * Math.PI) / 180);
  const metersPerDegLon = 111320 * cosLat0;
  const metersPerDegLat = 111320;

  if (!geometry) {
    const half = 80; // mètres — petit contexte de voisinage autour du point
    const minLon = lon - half / metersPerDegLon;
    const maxLon = lon + half / metersPerDegLon;
    const minLat = lat - half / metersPerDegLat;
    const maxLat = lat + half / metersPerDegLat;
    return { bbox: `${minLat},${minLon},${maxLat},${maxLon}`, pathD: '', size: PRINT_SAT_SIZE };
  }

  const polygons: [number, number][][][] =
    geometry.type === 'MultiPolygon' ? geometry.coordinates : [geometry.coordinates];
  const allPoints: [number, number][] = [];
  polygons.forEach(poly => poly.forEach(ring => ring.forEach(pt => allPoints.push(pt as [number, number]))));
  if (!allPoints.length) return null;

  const lat0 = allPoints.reduce((s, p) => s + p[1], 0) / allPoints.length;
  const lon0 = allPoints.reduce((s, p) => s + p[0], 0) / allPoints.length;
  const toMeters = (plon: number, plat: number): [number, number] => [
    (plon - lon0) * metersPerDegLon,
    (plat - lat0) * metersPerDegLat,
  ];
  const metersPoints = allPoints.map(([plon, plat]) => toMeters(plon, plat));
  const width = Math.max(...metersPoints.map(p => Math.abs(p[0]))) * 2;
  const height = Math.max(...metersPoints.map(p => Math.abs(p[1]))) * 2;
  const half = Math.max(width, height, 40) * 1.4;

  const minLon = lon0 - half / metersPerDegLon;
  const maxLon = lon0 + half / metersPerDegLon;
  const minLat = lat0 - half / metersPerDegLat;
  const maxLat = lat0 + half / metersPerDegLat;

  const project = (plon: number, plat: number): [number, number] => [
    ((plon - minLon) / (maxLon - minLon)) * PRINT_SAT_SIZE,
    ((maxLat - plat) / (maxLat - minLat)) * PRINT_SAT_SIZE,
  ];
  const ringToPath = (ring: [number, number][]) => {
    const pts = ring.map(([plon, plat]) => project(plon, plat));
    return 'M ' + pts.map(p => p.join(',')).join(' L ') + ' Z';
  };
  const pathD = polygons.map(poly => poly.map(ring => ringToPath(ring)).join(' ')).join(' ');

  return { bbox: `${minLat},${minLon},${maxLat},${maxLon}`, pathD, size: PRINT_SAT_SIZE };
}

// Carte satellite avec le bien (repère maison) + des points numérotés
// (comparables retenus) — même principe de projection équirectangulaire que
// computeSatelliteView, mais avec un cadrage qui englobe TOUS les points au
// lieu d'une seule parcelle.
function computeMultiPointMapView(
  subjectLon: number,
  subjectLat: number,
  points: { lon: number; lat: number }[]
): { bbox: string; size: number; subjectXY: [number, number]; pointsXY: [number, number][] } | null {
  if (!Number.isFinite(subjectLon) || !Number.isFinite(subjectLat)) return null;
  const size = 700;

  const allLon = [subjectLon, ...points.map(p => p.lon)];
  const allLat = [subjectLat, ...points.map(p => p.lat)];
  const lat0 = allLat.reduce((s, v) => s + v, 0) / allLat.length;
  const lon0 = allLon.reduce((s, v) => s + v, 0) / allLon.length;
  const cosLat0 = Math.cos((lat0 * Math.PI) / 180);
  const metersPerDegLon = 111320 * cosLat0;
  const metersPerDegLat = 111320;

  const toMeters = (plon: number, plat: number): [number, number] => [
    (plon - lon0) * metersPerDegLon,
    (plat - lat0) * metersPerDegLat,
  ];
  const metersPoints = [[subjectLon, subjectLat], ...points.map(p => [p.lon, p.lat] as [number, number])].map(([plon, plat]) => toMeters(plon, plat));
  const maxAbsX = Math.max(...metersPoints.map(p => Math.abs(p[0])), 60);
  const maxAbsY = Math.max(...metersPoints.map(p => Math.abs(p[1])), 60);
  const half = Math.max(maxAbsX, maxAbsY) * 1.25; // marge pour ne pas coller les pastilles au bord

  const minLon = lon0 - half / metersPerDegLon;
  const maxLon = lon0 + half / metersPerDegLon;
  const minLat = lat0 - half / metersPerDegLat;
  const maxLat = lat0 + half / metersPerDegLat;

  const project = (plon: number, plat: number): [number, number] => [
    ((plon - minLon) / (maxLon - minLon)) * size,
    ((maxLat - plat) / (maxLat - minLat)) * size,
  ];

  return {
    bbox: `${minLat},${minLon},${maxLat},${maxLon}`,
    size,
    subjectXY: project(subjectLon, subjectLat),
    pointsXY: points.map(p => project(p.lon, p.lat)),
  };
}

// Construit un chemin SVG (polyligne) pour une série de valeurs, sur une
// échelle Y PARTAGÉE avec les autres séries passées (permet de comparer
// plusieurs courbes, ex: OAT 10 ans / OAT 5 ans, sur le même repère) — SVG
// dessiné à la main plutôt qu'une lib de graphique (recharts n'est utilisé
// que dans le formulaire à l'écran ; sa capture par html2canvas dans le
// pipeline d'export PDF n'est pas fiable, contrairement à du SVG statique).
function buildSharedScaleLinePaths(
  seriesList: number[][],
  width: number,
  height: number,
  padding = 6
): { paths: string[]; min: number; max: number } {
  const all = seriesList.flat().filter((v) => Number.isFinite(v));
  if (all.length === 0) return { paths: seriesList.map(() => ''), min: 0, max: 0 };
  const min = Math.min(...all);
  const max = Math.max(...all);
  const range = max - min || 1;
  const maxLen = Math.max(...seriesList.map((s) => s.length), 1);
  const stepX = (width - padding * 2) / Math.max(maxLen - 1, 1);
  const paths = seriesList.map((values) =>
    values
      .map((v, i) => {
        const x = padding + i * stepX;
        const y = height - padding - ((v - min) / range) * (height - padding * 2);
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ')
  );
  return { paths, min, max };
}

// Section du rapport, style "document" (titre + petite barre d'accent +
// filet fin) plutôt qu'un encadré façon widget d'app — cohérent avec la
// mise en page d'une étude de marché imprimée.
function PB({ icon, title, children }: { icon?: string; title: string; children: React.ReactNode }) {
  return (
    <div className="print-block mb-6">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
        <span className="w-1 h-5 rounded-sm flex-shrink-0" style={{ backgroundColor: PRINT_BRAND }} />
        <h2 className="text-[15px] font-bold text-gray-900">{icon ? `${icon} ` : ''}{title}</h2>
      </div>
      <div>{children}</div>
    </div>
  );
}

// Bandeau de titre en haut de chaque page du rapport (hors couverture) —
// dégradé orange SAFTI pleine largeur, façon en-tête de document imprimé.
function PageHeader({ title }: { title: string }) {
  return (
    <div className="print-block -mx-5 px-6 py-3 mb-5 text-right" style={{ background: `linear-gradient(90deg, ${PRINT_BRAND}, #d9612f)` }}>
      <span className="text-white text-xs font-bold uppercase tracking-widest">{title}</span>
    </div>
  );
}

// Ligne "chapitre ⋯⋯⋯⋯ n°" du sommaire, avec filet pointillé menant au numéro.
function SommaireLigne({ label, numero }: { label: string; numero: number }) {
  return (
    <div className="flex items-baseline gap-2 py-1">
      <span className="text-sm text-gray-700">{label}</span>
      <span className="flex-1 border-b border-dotted border-gray-300 translate-y-[-3px]" />
      <span className="text-sm font-semibold" style={{ color: PRINT_BRAND }}>{numero}</span>
    </div>
  );
}

// Marqueur de saut de page forcé — repéré par generatePdfBlob() pour
// démarrer une nouvelle page PDF à cet endroit précis, quelle que soit la
// hauteur déjà remplie sur la page courante.
function PageBreak() {
  return <div className="pdf-force-break" />;
}

const FORM_TABS: { key: string; label: string }[] = [
  { key: 'client', label: 'Client' },
  { key: 'bien', label: 'Bien immobilier' },
  { key: 'caracteristiques', label: 'Caractéristiques' },
  { key: 'estimation', label: 'Estimation' },
  { key: 'environnement', label: 'Environnement' },
  { key: 'suivi', label: 'Suivi commercial' },
];

export default function EstimationPage() {
  const [view, setView] = useState<'liste' | 'form'>('liste');
  const [dossiers, setDossiers] = useState<Dossier[]>([]);
  const [loadingDossiers, setLoadingDossiers] = useState(false);
  const [agentProfile, setAgentProfile] = useState<AgentProfile | null>(null);
  const [formTab, setFormTab] = useState('client');

  useEffect(() => {
    fetch(`${CADASTRE_API}/agent-profile`, { headers: headers() })
      .then(res => res.json())
      .then(data => { if (data.success) setAgentProfile(data.agent); })
      .catch(() => { /* silencieux — bonus uniquement */ });
  }, []);

  const loadDossiers = async () => {
    setLoadingDossiers(true);
    try {
      const res = await fetch(`${API}/dossiers`, { headers: headers() });
      const data = await res.json();
      if (data.success) setDossiers(data.dossiers);
    } catch {
      // silencieux
    }
    setLoadingDossiers(false);
  };

  useEffect(() => { loadDossiers(); }, []);

  // ── Formulaire dossier (création ou édition) ──
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Draft>(BLANK_DRAFT);
  const [statut, setStatut] = useState('brouillon');
  const [comparables, setComparables] = useState<Comparable[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [resultat, setResultat] = useState<Resultat | null>(null);
  const [comparablesLoading, setComparablesLoading] = useState(false);
  const [comparablesError, setComparablesError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [emailTouched, setEmailTouched] = useState(false);

  // Suivi commercial
  const [activites, setActivites] = useState<Activite[]>([]);
  const [activitesLoading, setActivitesLoading] = useState(false);
  const [noteInput, setNoteInput] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  // Environnement
  const [environnement, setEnvironnement] = useState<Environnement | null>(null);
  const [environnementLoading, setEnvironnementLoading] = useState(false);

  // Urbanisme & cadastre (pour la page "Urbanisme et cadastre" du rapport)
  const [urbanisme, setUrbanisme] = useState<Urbanisme | null>(null);
  const [urbanismeLoading, setUrbanismeLoading] = useState(false);
  // Incrémenté à chaque appel de chargerUrbanisme — permet à un sondage en
  // cours (retry pendant l'import initial INSEE/Sit@del2/OAT) de se stopper
  // proprement si l'agent change de dossier/adresse entre-temps.
  const urbanismeRequestRef = useRef(0);

  // Analyse IA (Gemini)
  const [analyseIa, setAnalyseIa] = useState('');
  const [analyseIaLoading, setAnalyseIaLoading] = useState(false);
  const [analyseIaError, setAnalyseIaError] = useState<string | null>(null);
  // Dossiers pour lesquels la génération auto a déjà été tentée (évite de
  // regénérer en boucle à chaque re-render, et de re-tenter après un échec
  // silencieux tant que l'agent ne clique pas lui-même sur "Régénérer").
  const analyseIaAutoTenteRef = useRef<Set<number>>(new Set());

  // Envoi du rapport par email au demandeur
  const [envoiEmailLoading, setEnvoiEmailLoading] = useState(false);
  const [envoiEmailMsg, setEnvoiEmailMsg] = useState<string | null>(null);

  // Autocomplétion adresse (identique au Cadastre)
  const [addressInput, setAddressInput] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const skipNextSuggest = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (skipNextSuggest.current) { skipNextSuggest.current = false; return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (addressInput.trim().length < 3) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${CADASTRE_API}/suggest?q=${encodeURIComponent(addressInput.trim())}`, { headers: headers() });
        const data = await res.json();
        if (data.success) { setSuggestions(data.suggestions); setShowSuggestions(true); }
      } catch { /* silencieux */ }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [addressInput]);

  const selectSuggestion = (s: Suggestion) => {
    skipNextSuggest.current = true;
    setAddressInput(s.label);
    setSuggestions([]);
    setShowSuggestions(false);
    setDraft(d => ({ ...d, adresseLabel: s.label, lon: s.lon, lat: s.lat, codeInsee: s.citycode, codeDep: s.citycode?.slice(0, 2) || null }));
    // Adresse changée → les comparables et l'environnement précédents ne sont plus pertinents
    setComparables([]);
    setSelectedIds(new Set());
    setResultat(null);
    setEnvironnement(null);
    setAnalyseIa('');
    setUrbanisme(null);
    // Auto-remplissage best-effort, sans bloquer la saisie : environnement +
    // année de construction/DPE existant, sans attendre que l'agent clique quoi que ce soit.
    chargerEnvironnement(s.lon, s.lat);
    chargerAutoInfos(s.lon, s.lat, s.citycode);
    chargerUrbanisme(s.lon, s.lat, s.label, s.city, s.postcode, s.citycode);
  };

  function nouveauDossier() {
    setCurrentId(null);
    setDraft(BLANK_DRAFT);
    setStatut('brouillon');
    setAddressInput('');
    setComparables([]);
    setSelectedIds(new Set());
    setResultat(null);
    setSaveMsg(null);
    setComparablesError(null);
    setEmailTouched(false);
    setActivites([]);
    setEnvironnement(null);
    setAnalyseIa('');
    setAnalyseIaError(null);
    setUrbanisme(null);
    setFormTab('client');
    setView('form');
  }

  async function ouvrirDossier(id: number) {
    try {
      const res = await fetch(`${API}/dossiers/${id}`, { headers: headers() });
      const data = await res.json();
      if (!data.success) return;
      const d: Dossier = data.dossier;
      setCurrentId(d.id);
      setStatut(d.statut);
      const ajustements = { ...BLANK_AJUSTEMENTS };
      const fromServer = d.ajustements_prix || {};
      for (const k of AJUSTEMENT_ORDER) {
        if (fromServer[k]) ajustements[k] = { mode: (fromServer[k].mode as 'euro' | 'pct') || 'euro', valeur: String(fromServer[k].valeur) };
      }
      setDraft({
        clientNom: d.client_nom || '',
        clientEmail: d.client_email || '',
        motif: d.motif,
        adresseLabel: d.adresse_label,
        codeInsee: d.code_insee,
        codeDep: d.code_dep,
        lon: d.lon,
        lat: d.lat,
        typeBien: d.type_bien,
        surfaceHabitable: String(d.surface_habitable),
        nbPieces: d.nb_pieces != null ? String(d.nb_pieces) : '',
        anneeConstruction: d.annee_construction != null ? String(d.annee_construction) : '',
        etatGeneral: d.etat_general,
        etage: d.etage || '',
        exposition: d.exposition || '',
        travauxRecents: d.travaux_recents,
        dpeLettre: d.dpe_lettre || '',
        notes: d.notes || '',
        demandeurTelephone: d.demandeur_telephone || '',
        demandeurAdresse: d.demandeur_adresse || '',
        proprietaireEstDemandeur: d.proprietaire_est_demandeur,
        proprietaireNom: d.proprietaire_nom || '',
        proprietaireTelephone: d.proprietaire_telephone || '',
        proprietaireEmail: d.proprietaire_email || '',
        etatAvancement: d.etat_avancement || 'a_contacter',
        criteresQualitatifs: d.criteres_qualitatifs || {},
        ajustements,
        methodeComparaisonActive: d.methode_comparaison_active,
        methodeCapitalisationActive: d.methode_capitalisation_active,
        loyerMensuelEstime: d.loyer_mensuel_estime != null ? String(d.loyer_mensuel_estime) : '',
        tauxRendementPct: d.taux_rendement_pct != null ? String(d.taux_rendement_pct) : '5',
        honorairesPct: d.honoraires_pct != null ? String(d.honoraires_pct) : '5',
        affichageNetVendeur: d.affichage_net_vendeur,
      });
      setAddressInput(d.adresse_label);
      const retenus = d.comparables_retenus || [];
      setComparables(retenus);
      setSelectedIds(new Set(retenus.map(c => c.idMutation)));
      setResultat({
        prixM2Base: d.prix_m2_base,
        coefficientAjustement: d.coefficient_ajustement,
        prixBas: d.prix_bas,
        prixHaut: d.prix_haut,
        prixRetenu: d.prix_retenu,
        prixCapitalisation: d.prix_capitalisation,
        indiceQualitePct: d.indice_qualite_pct,
        montantFinal: d.montant_final,
      });
      setSaveMsg(null);
      setComparablesError(null);
      setEmailTouched(false);
      setActivites([]);
      setEnvironnement(null);
      setAnalyseIa(d.analyse_ia || '');
      setAnalyseIaError(null);
      setUrbanisme(null);
      setFormTab('client');
      setView('form');
      if (d.lon != null && d.lat != null) {
        // Chargés proactivement (et pas seulement au clic sur l'onglet
        // Environnement) pour que le rapport PDF ne soit jamais tronqué
        // silencieusement si l'agent exporte sans avoir visité cet onglet.
        chargerUrbanisme(d.lon, d.lat, d.adresse_label, '', '', d.code_insee || '');
        chargerEnvironnement(d.lon, d.lat);
      }
    } catch {
      // silencieux
    }
  }

  async function rechercherComparables() {
    if (draft.lon == null || draft.lat == null) {
      setComparablesError('Sélectionne d\'abord une adresse dans la liste de suggestions.');
      return;
    }
    if (!draft.surfaceHabitable) {
      setComparablesError('Renseigne la surface habitable avant de chercher des comparables.');
      return;
    }
    setComparablesLoading(true);
    setComparablesError(null);
    try {
      const params = new URLSearchParams({
        lon: String(draft.lon),
        lat: String(draft.lat),
        typeBien: draft.typeBien,
        surface: draft.surfaceHabitable,
      });
      const res = await fetch(`${API}/comparables?${params.toString()}`, { headers: headers() });
      const data = await res.json();
      if (data.success) {
        setComparables(data.comparables);
        // Sélection par défaut : les 8 comparables les plus proches
        const top = data.comparables.slice(0, 8).map((c: Comparable) => c.idMutation);
        setSelectedIds(new Set(top));
        if (data.comparables.length === 0) {
          setComparablesError('Aucune vente comparable trouvée dans un rayon de 2 km avec ces critères — élargis le rayon ou vérifie le type de bien / la surface.');
        }
      } else {
        setComparablesError(data.error || 'Recherche de comparables échouée');
      }
    } catch {
      setComparablesError('Erreur réseau');
    }
    setComparablesLoading(false);
  }

  function toggleComparable(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function updateCritere(catKey: string, critKey: string, value: number) {
    setDraft(d => ({
      ...d,
      criteresQualitatifs: { ...d.criteresQualitatifs, [catKey]: { ...(d.criteresQualitatifs[catKey] || {}), [critKey]: value } },
    }));
  }

  function updateAjustement(key: string, patch: Partial<AjustementLigne>) {
    setDraft(d => ({ ...d, ajustements: { ...d.ajustements, [key]: { ...d.ajustements[key], ...patch } } }));
  }

  const comparablesRetenus = comparables.filter(c => selectedIds.has(c.idMutation));

  // Dès que l'adresse, le type de bien et la surface sont tous connus, lance la
  // recherche de comparables automatiquement — plus besoin de cliquer si l'agent
  // a rempli l'adresse en dernier (ou la surface en dernier), dans n'importe quel ordre.
  useEffect(() => {
    if (draft.lon != null && draft.lat != null && draft.surfaceHabitable && comparables.length === 0 && !comparablesLoading) {
      rechercherComparables();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.lon, draft.lat, draft.typeBien, draft.surfaceHabitable]);

  // Recalcul du résultat dès que la sélection, les critères ou les ajustements changent (débounce léger)
  const calculDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const capitalisationUtilisable = draft.methodeCapitalisationActive && !!draft.loyerMensuelEstime && !!draft.tauxRendementPct;
    const comparaisonUtilisable = comparablesRetenus.length > 0 && !!draft.surfaceHabitable;
    if (!comparaisonUtilisable && !capitalisationUtilisable) { setResultat(null); return; }
    if (calculDebounceRef.current) clearTimeout(calculDebounceRef.current);
    calculDebounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/calculer`, {
          method: 'POST',
          headers: jsonHeaders(),
          body: JSON.stringify({
            comparablesRetenus,
            surfaceHabitable: parseFloat(draft.surfaceHabitable) || 0,
            etatGeneral: draft.etatGeneral,
            etage: draft.etage || undefined,
            exposition: draft.exposition || undefined,
            travauxRecents: draft.travauxRecents,
            dpeLettre: draft.dpeLettre || undefined,
            criteresQualitatifs: draft.criteresQualitatifs,
            ajustementsPrix: buildAjustementsPrix(draft.ajustements),
            methodeComparaisonActive: draft.methodeComparaisonActive,
            methodeCapitalisationActive: draft.methodeCapitalisationActive,
            loyerMensuelEstime: draft.loyerMensuelEstime ? parseFloat(draft.loyerMensuelEstime) : undefined,
            tauxRendementPct: draft.tauxRendementPct ? parseFloat(draft.tauxRendementPct) : undefined,
            honorairesPct: draft.honorairesPct ? parseFloat(draft.honorairesPct) : undefined,
          }),
        });
        const data = await res.json();
        if (data.success) {
          setResultat({
            prixM2Base: data.prixM2Base, coefficientAjustement: data.coefficientAjustement,
            prixBas: data.prixBas, prixHaut: data.prixHaut, prixRetenu: data.prixRetenu,
            prixCapitalisation: data.prixCapitalisation, indiceQualitePct: data.indiceQualitePct,
            montantFinal: data.montantFinal,
          });
        }
      } catch {
        // silencieux
      }
    }, 350);
    return () => { if (calculDebounceRef.current) clearTimeout(calculDebounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedIds, draft.surfaceHabitable, draft.etatGeneral, draft.etage, draft.exposition, draft.travauxRecents, draft.dpeLettre,
    draft.criteresQualitatifs, draft.ajustements, draft.methodeComparaisonActive, draft.methodeCapitalisationActive,
    draft.loyerMensuelEstime, draft.tauxRendementPct, draft.honorairesPct,
  ]);

  const montantAffiche = resultat?.montantFinal != null
    ? (draft.affichageNetVendeur ? resultat.montantFinal : Math.round(resultat.montantFinal * (1 + (parseFloat(draft.honorairesPct) || 0) / 100)))
    : (resultat?.prixRetenu ?? null);

  async function enregistrerDossier() {
    if (!draft.adresseLabel || draft.lon == null || draft.lat == null) {
      setSaveMsg('❌ Sélectionne une adresse valide avant d\'enregistrer.');
      return;
    }
    if (!draft.surfaceHabitable) {
      setSaveMsg('❌ Renseigne la surface habitable.');
      return;
    }
    if (!isEmailValide(draft.clientEmail)) {
      setEmailTouched(true);
      setSaveMsg('❌ L\'adresse email du client est invalide.');
      return;
    }
    setSaving(true);
    setSaveMsg(null);
    const payload = {
      clientNom: draft.clientNom || null,
      clientEmail: draft.clientEmail || null,
      motif: draft.motif,
      adresseLabel: draft.adresseLabel,
      codeInsee: draft.codeInsee,
      codeDep: draft.codeDep,
      lon: draft.lon,
      lat: draft.lat,
      typeBien: draft.typeBien,
      surfaceHabitable: parseFloat(draft.surfaceHabitable),
      nbPieces: draft.nbPieces ? parseInt(draft.nbPieces, 10) : null,
      anneeConstruction: draft.anneeConstruction ? parseInt(draft.anneeConstruction, 10) : null,
      etatGeneral: draft.etatGeneral,
      etage: draft.etage || null,
      exposition: draft.exposition || null,
      travauxRecents: draft.travauxRecents,
      dpeLettre: draft.dpeLettre || null,
      comparablesRetenus,
      notes: draft.notes || null,
      analyseIa: analyseIa || null,
      statut,
      demandeurTelephone: draft.demandeurTelephone || null,
      demandeurAdresse: draft.demandeurAdresse || null,
      proprietaireEstDemandeur: draft.proprietaireEstDemandeur,
      proprietaireNom: draft.proprietaireEstDemandeur ? null : (draft.proprietaireNom || null),
      proprietaireTelephone: draft.proprietaireEstDemandeur ? null : (draft.proprietaireTelephone || null),
      proprietaireEmail: draft.proprietaireEstDemandeur ? null : (draft.proprietaireEmail || null),
      etatAvancement: draft.etatAvancement,
      criteresQualitatifs: draft.criteresQualitatifs,
      ajustementsPrix: buildAjustementsPrix(draft.ajustements),
      methodeComparaisonActive: draft.methodeComparaisonActive,
      methodeCapitalisationActive: draft.methodeCapitalisationActive,
      loyerMensuelEstime: draft.loyerMensuelEstime ? parseFloat(draft.loyerMensuelEstime) : null,
      tauxRendementPct: draft.tauxRendementPct ? parseFloat(draft.tauxRendementPct) : null,
      honorairesPct: draft.honorairesPct ? parseFloat(draft.honorairesPct) : null,
      affichageNetVendeur: draft.affichageNetVendeur,
    };
    try {
      const res = currentId
        ? await fetch(`${API}/dossiers/${currentId}`, { method: 'PATCH', headers: jsonHeaders(), body: JSON.stringify(payload) })
        : await fetch(`${API}/dossiers`, { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(payload) });
      const data = await res.json();
      if (data.success) {
        setCurrentId(data.dossier.id);
        setSaveMsg('✅ Dossier enregistré.');
        loadDossiers();
        if (formTab === 'suivi') loadActivites(data.dossier.id);
      } else {
        setSaveMsg(`❌ ${data.error || 'Échec de l\'enregistrement'}`);
      }
    } catch {
      setSaveMsg('❌ Erreur réseau');
    }
    setSaving(false);
  }

  async function supprimerDossier(id: number) {
    try {
      const res = await fetch(`${API}/dossiers/${id}`, { method: 'DELETE', headers: headers() });
      const data = await res.json();
      if (data.success) {
        setConfirmDeleteId(null);
        if (currentId === id) { setView('liste'); setCurrentId(null); }
        loadDossiers();
      }
    } catch {
      // silencieux
    }
  }

  // ── Suivi commercial ──
  async function loadActivites(id: number) {
    setActivitesLoading(true);
    try {
      const res = await fetch(`${API}/dossiers/${id}/activites`, { headers: headers() });
      const data = await res.json();
      if (data.success) setActivites(data.activites);
    } catch {
      // silencieux
    }
    setActivitesLoading(false);
  }

  async function ajouterNote() {
    if (!currentId || !noteInput.trim()) return;
    setAddingNote(true);
    try {
      const res = await fetch(`${API}/dossiers/${currentId}/activites`, {
        method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ description: noteInput.trim() }),
      });
      const data = await res.json();
      if (data.success) { setActivites(data.activites); setNoteInput(''); }
    } catch {
      // silencieux
    }
    setAddingNote(false);
  }

  function ouvrirOnglet(tab: string) {
    setFormTab(tab);
    if (tab === 'suivi' && currentId) loadActivites(currentId);
    if (tab === 'environnement' && !environnement && !environnementLoading && draft.lon != null) chargerEnvironnement();
  }

  // Tant que l'adresse et la surface ne sont pas encore renseignées (souvent
  // sur un autre onglet que celui affiché), le bouton principal ne doit pas
  // proposer "Enregistrer" — ça échoue systématiquement et prête à confusion.
  // Il propose plutôt de passer à l'onglet suivant ; une fois les champs requis
  // présents (ou sur le dernier onglet), il devient le vrai bouton d'enregistrement.
  const formTabIndex = FORM_TABS.findIndex(t => t.key === formTab);
  const dernierOnglet = formTabIndex === FORM_TABS.length - 1;
  const peutEnregistrer = !!draft.adresseLabel && draft.lon != null && draft.lat != null && !!draft.surfaceHabitable;

  // Le PDF capture le DOM à l'instant T (html2canvas) : s'il est généré
  // pendant qu'un des chargements async est encore en cours, le rapport se
  // retrouve figé avec des "chargement en cours..." à la place des vraies
  // données. On bloque donc l'export tant que tout n'a pas fini de charger.
  const rapportEnCoursDeChargement = urbanismeLoading || environnementLoading || comparablesLoading;

  function suivantOuEnregistrer() {
    if (!peutEnregistrer && !dernierOnglet) {
      ouvrirOnglet(FORM_TABS[formTabIndex + 1].key);
      return;
    }
    enregistrerDossier();
  }

  // ── Environnement (POI, écoles, gare, autoroute) ──
  async function chargerEnvironnement(lonArg?: number, latArg?: number) {
    const lon = lonArg ?? draft.lon;
    const lat = latArg ?? draft.lat;
    if (lon == null || lat == null) return;
    setEnvironnementLoading(true);
    try {
      const params = new URLSearchParams({ lon: String(lon), lat: String(lat) });
      const res = await fetch(`${API}/environnement?${params.toString()}`, { headers: headers() });
      const data = await res.json();
      if (data.success) setEnvironnement({ poi: data.poi, ecoles: data.ecoles, gare: data.gare, autoroute: data.autoroute });
    } catch {
      // silencieux
    }
    setEnvironnementLoading(false);
  }

  // Urbanisme & cadastre (référence parcellaire + zonage PLU) — réutilise
  // l'endpoint existant du module Cadastre, best-effort, jamais bloquant.
  //
  // Certaines données (logement INSEE / constructions Sit@del2 / taux OAT)
  // sont importées en base à la demande côté serveur : au tout premier accès
  // à une commune (ou juste après un déploiement), l'API répond tout de
  // suite avec status "importing" pendant que l'import tourne en tâche de
  // fond (peut prendre jusqu'à 1-2 minutes pour ces gros fichiers nationaux).
  // On sonde donc à nouveau l'endpoint toutes les 8s tant qu'au moins une de
  // ces sources est "importing", pour que la page (et donc l'export PDF, qui
  // capture le DOM à l'instant T) affiche les vraies données réelles dès
  // qu'elles sont prêtes, sans que l'agent ait à rafraîchir manuellement.
  async function chargerUrbanisme(lon: number, lat: number, label: string, city: string, postcode: string, citycode: string) {
    const requestId = ++urbanismeRequestRef.current;
    const MAX_TENTATIVES = 8; // ~8 x 8s ≈ 1min quinze de sondage max
    setUrbanismeLoading(true);

    async function tenter(tentative: number): Promise<void> {
      try {
        const params = new URLSearchParams({ lon: String(lon), lat: String(lat), label: label || '', city: city || '', postcode: postcode || '', citycode: citycode || '' });
        const res = await fetch(`${CADASTRE_API}/lookup?${params.toString()}`, { headers: headers() });
        const data = await res.json();
        if (requestId !== urbanismeRequestRef.current) return; // dossier/adresse changé entre-temps

        if (data.success) {
          setUrbanisme({
            parcelle: data.parcelle || null,
            zonage: data.zonage || null,
            batiments: data.batiments || [],
            eligibiliteInternet: data.eligibiliteInternet || null,
            logementCommune: data.logementCommune || null,
            constructionsNeuves: data.constructionsNeuves || null,
            tauxOAT: data.tauxOAT || null,
          });

          const encoreEnImport = [data.logementCommune?.status, data.constructionsNeuves?.status, data.tauxOAT?.status]
            .some((s) => s === 'importing');
          if (encoreEnImport && tentative < MAX_TENTATIVES) {
            setTimeout(() => { tenter(tentative + 1); }, 8000);
            return; // reste "loading" pendant le sondage
          }
        }
      } catch {
        // silencieux — la fiche cadastrale reste optionnelle dans le rapport
      }
      if (requestId === urbanismeRequestRef.current) setUrbanismeLoading(false);
    }

    await tenter(0);
  }

  // Pré-remplissage automatique (année de construction BDNB, DPE existant ADEME) dès
  // que l'adresse est connue — ne remplace jamais une valeur déjà saisie par l'agent.
  async function chargerAutoInfos(lon: number, lat: number, codeInsee: string) {
    try {
      const params = new URLSearchParams({ lon: String(lon), lat: String(lat), codeInsee: codeInsee || '' });
      const res = await fetch(`${API}/auto-infos?${params.toString()}`, { headers: headers() });
      const data = await res.json();
      if (!data.success) return;
      setDraft(d => ({
        ...d,
        anneeConstruction: !d.anneeConstruction && data.anneeConstruction != null ? String(data.anneeConstruction) : d.anneeConstruction,
        dpeLettre: !d.dpeLettre && data.dpe?.etiquette ? data.dpe.etiquette : d.dpeLettre,
        surfaceHabitable: !d.surfaceHabitable && data.dpe?.surfaceHabitable ? String(data.dpe.surfaceHabitable) : d.surfaceHabitable,
      }));
    } catch {
      // silencieux — pré-remplissage best-effort, jamais bloquant
    }
  }

  // ── Export PDF (même principe que la fiche cadastrale) ──
  const printRef = useRef<HTMLDivElement>(null);
  const [sharing, setSharing] = useState(false);

  async function generatePdfBlob(): Promise<Blob | null> {
    const node = printRef.current;
    if (!node) return null;
    node.classList.add('pdf-capture');
    try {
      const blockEls = Array.from(node.querySelectorAll<HTMLElement>('.print-block, .print-only'));
      const nodeTop = node.getBoundingClientRect().top;
      const unbreakable = blockEls
        .map(el => { const r = el.getBoundingClientRect(); return { start: r.top - nodeTop, end: r.bottom - nodeTop }; })
        .sort((a, b) => a.start - b.start);

      // Sauts de page forcés (couverture, sommaire, chaque section majeure du
      // rapport) — chaque marqueur .pdf-force-break démarre une toute
      // nouvelle page PDF, quelle que soit la place restante sur la page en cours.
      const breakEls = Array.from(node.querySelectorAll<HTMLElement>('.pdf-force-break'));
      const forcedBreaks = breakEls
        .map(el => el.getBoundingClientRect().top - nodeTop)
        .sort((a, b) => a - b);

      const canvas = await html2canvas(node, { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false });
      const pxPerCssPx = canvas.width / node.offsetWidth;
      const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
      const pageWidthMm = pdf.internal.pageSize.getWidth();
      const pageHeightMm = pdf.internal.pageSize.getHeight();
      const pageHeightPx = pageHeightMm * (canvas.width / pageWidthMm);

      // Pied de page dessiné directement dans le PDF (texte jsPDF, pas du
      // DOM) — toujours positionné au même endroit sur CHAQUE page, quelle
      // que soit la façon dont le contenu se découpe. Ça évite les soucis
      // rencontrés avec un pied de page en HTML (texte coupé en deux, ou
      // page presque vide générée juste pour le faire tenir en entier).
      const nomDistinct = agentProfile?.name && agentProfile.name !== agentProfile?.entreprise ? agentProfile.name : '';
      const footerGauche1 = agentProfile?.entreprise || agentProfile?.name || 'SAFTI';
      const footerGauche2 = [nomDistinct, agentProfile?.email, agentProfile?.telephone].filter(Boolean).join('  ·  ');
      const footerDroite1 = `Bien situé à ${draft.adresseLabel || '—'}`;
      const footerDroite2 = generatedAt.toLocaleDateString('fr-FR');
      const drawFooter = () => {
        const footerY = pageHeightMm - 10;
        pdf.setDrawColor(230, 230, 230);
        pdf.line(12, footerY - 2.5, pageWidthMm - 12, footerY - 2.5);
        pdf.setFontSize(6.5);
        pdf.setTextColor(150, 150, 150);
        pdf.text(footerGauche1, 12, footerY);
        if (footerGauche2) pdf.text(footerGauche2, 12, footerY + 3.2);
        pdf.text(footerDroite1, pageWidthMm - 12, footerY, { align: 'right' });
        pdf.text(footerDroite2, pageWidthMm - 12, footerY + 3.2, { align: 'right' });
      };

      let y = 0;
      let firstPage = true;
      let pageIndex = 0;
      while (y < canvas.height) {
        let sliceEnd = Math.min(y + pageHeightPx, canvas.height);
        // Un saut forcé strictement après le début de la page courante et
        // avant la fin naturelle de page prend le dessus : on coupe là.
        for (const breakPx of forcedBreaks.map(b => b * pxPerCssPx)) {
          if (breakPx > y && breakPx < sliceEnd) { sliceEnd = breakPx; break; }
        }
        for (const zone of unbreakable) {
          const zoneStartPx = zone.start * pxPerCssPx;
          const zoneEndPx = zone.end * pxPerCssPx;
          if (zoneStartPx < sliceEnd && zoneEndPx > sliceEnd && zoneStartPx > y) sliceEnd = zoneStartPx;
        }
        const sliceHeight = Math.max(1, Math.round(sliceEnd - y));
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceHeight;
        const ctx = pageCanvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
          ctx.drawImage(canvas, 0, y, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);
        }
        const sliceImgData = pageCanvas.toDataURL('image/jpeg', 0.92);
        const sliceHeightMm = (sliceHeight / canvas.width) * pageWidthMm;
        if (!firstPage) pdf.addPage();
        pdf.addImage(sliceImgData, 'JPEG', 0, 0, pageWidthMm, sliceHeightMm);
        if (pageIndex > 0) drawFooter(); // pas sur la couverture, déjà bandeau orange dédié
        firstPage = false;
        pageIndex += 1;
        y = sliceEnd;
      }
      return pdf.output('blob');
    } finally {
      node.classList.remove('pdf-capture');
    }
  }

  function fileName() {
    return `estimation-${(draft.adresseLabel || 'dossier').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.pdf`;
  }

  async function tracerPdfGenere() {
    if (!currentId) return;
    try { await fetch(`${API}/dossiers/${currentId}/pdf-genere`, { method: 'POST', headers: headers() }); } catch { /* silencieux */ }
  }

  // ── Analyse IA (Gemini) — rédige le paragraphe d'analyse du rapport ──
  async function genererAnalyseIa() {
    if (!currentId) {
      setAnalyseIaError('Enregistre le dossier avant de générer l\'analyse IA.');
      return;
    }
    setAnalyseIaLoading(true);
    setAnalyseIaError(null);
    try {
      const res = await fetch(`${API}/dossiers/${currentId}/generer-analyse`, {
        method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ environnement: environnement || undefined }),
      });
      const data = await res.json();
      if (data.success) {
        setAnalyseIa(data.analyseIa);
      } else {
        setAnalyseIaError(data.error || 'Génération échouée');
      }
    } catch {
      setAnalyseIaError('Erreur réseau');
    }
    setAnalyseIaLoading(false);
  }

  // Génère automatiquement le texte "En résumé" dès que le dossier a une
  // estimation calculée, sans attendre que l'agent clique sur "Générer (IA)"
  // — l'agent voit le texte directement en arrivant sur le bien, et peut
  // toujours le modifier ou le régénérer manuellement ensuite. Une seule
  // tentative automatique par dossier (voir analyseIaAutoTenteRef) : si Gemini
  // échoue, on n'insiste pas silencieusement en boucle, l'agent verra
  // l'erreur et pourra relancer via le bouton.
  useEffect(() => {
    if (!currentId || !montantAffiche || analyseIa || analyseIaLoading) return;
    if (analyseIaAutoTenteRef.current.has(currentId)) return;
    analyseIaAutoTenteRef.current.add(currentId);
    genererAnalyseIa();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId, montantAffiche, analyseIa, analyseIaLoading]);

  async function handleTelecharger() {
    if (rapportEnCoursDeChargement) return; // garde-fou (le bouton est normalement déjà désactivé)
    const blob = await generatePdfBlob();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName();
    a.click();
    URL.revokeObjectURL(url);
    tracerPdfGenere();
  }

  async function handlePartager() {
    if (rapportEnCoursDeChargement) return; // garde-fou (le bouton est normalement déjà désactivé)
    setSharing(true);
    try {
      const blob = await generatePdfBlob();
      if (blob) {
        const file = new File([blob], fileName(), { type: 'application/pdf' });
        if ((navigator as any).canShare && (navigator as any).canShare({ files: [file] })) {
          try {
            await (navigator as any).share({ files: [file], title: 'Estimation immobilière', text: draft.adresseLabel });
          } catch { /* annulé par l'utilisateur */ }
          tracerPdfGenere();
          setSharing(false);
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName();
        a.click();
        URL.revokeObjectURL(url);
        tracerPdfGenere();
      }
    } finally {
      setSharing(false);
    }
  }

  async function envoyerParEmail() {
    if (rapportEnCoursDeChargement || envoiEmailLoading) return;
    if (!currentId) { setEnvoiEmailMsg('❌ Enregistre le dossier avant d\'envoyer le rapport.'); return; }
    if (!draft.clientEmail) { setEnvoiEmailMsg('❌ Renseigne l\'email du demandeur (onglet Demandeur).'); return; }
    setEnvoiEmailLoading(true);
    setEnvoiEmailMsg(null);
    try {
      const blob = await generatePdfBlob();
      if (!blob) { setEnvoiEmailMsg('❌ Échec de génération du PDF.'); return; }
      const form = new FormData();
      form.append('pdf', blob, fileName());
      form.append('filename', fileName());
      const res = await fetch(`${API}/dossiers/${currentId}/envoyer-rapport`, { method: 'POST', headers: headers(), body: form });
      const data = await res.json();
      if (data.success) {
        setEnvoiEmailMsg(`✅ Rapport envoyé à ${draft.clientEmail}`);
        tracerPdfGenere();
      } else {
        setEnvoiEmailMsg(`❌ ${data.error || 'Envoi échoué'}`);
      }
    } catch {
      setEnvoiEmailMsg('❌ Erreur réseau');
    }
    setEnvoiEmailLoading(false);
  }

  const generatedAt = new Date();
  const radarData = computeRadarData(draft.criteresQualitatifs);
  const aCriteresRenseignes = radarData.some(r => r.note);
  const satView = draft.lon != null && draft.lat != null
    ? computeSatelliteView(urbanisme?.parcelle?.geometry ?? null, draft.lon, draft.lat)
    : null;
  const comparablesMapView = comparablesRetenus.length && draft.lon != null && draft.lat != null
    ? computeMultiPointMapView(draft.lon, draft.lat, comparablesRetenus.map(c => ({ lon: c.lon, lat: c.lat })))
    : null;
  // Bâtiment principal de la parcelle (le plus grand par emprise au sol —
  // écarte les annexes/garages) pour enrichir la page "Bien estimé" avec des
  // faits déjà récupérés via BDNB (nombre d'étages, matériaux...).
  const batimentPrincipal = urbanisme?.batiments?.length
    ? [...urbanisme.batiments].sort((a, b) => (b.empriseSolM2 || 0) - (a.empriseSolM2 || 0))[0]
    : null;

  const docTitle = COVER_TITLES[draft.motif] || 'ESTIMATION IMMOBILIÈRE';

  // Graphique taux OAT (10 ans / 5 ans) — chemins SVG pré-calculés sur une
  // échelle Y partagée, uniquement si les 2 séries (données Banque de
  // France réelles) sont disponibles ; sinon la page l'indique clairement
  // plutôt que d'afficher un graphique vide ou inventé.
  const OAT_CHART_W = 520;
  const OAT_CHART_H = 140;
  const oatSerie10 = (urbanisme?.tauxOAT?.serie10 || []).slice(-96);
  const oatSerie5 = (urbanisme?.tauxOAT?.serie5 || []).slice(-96);
  const oatChart = urbanisme?.tauxOAT?.status === 'ready' && (oatSerie10.length > 1 || oatSerie5.length > 1)
    ? buildSharedScaleLinePaths([oatSerie10.map(p => p.valeur), oatSerie5.map(p => p.valeur)], OAT_CHART_W, OAT_CHART_H)
    : null;

  const logementDonnees = urbanisme?.logementCommune?.donnees;
  const logementTotal = logementDonnees?.total_residences_principales || 0;
  const pieceBars = logementDonnees ? [
    { label: '1 pièce', valeur: logementDonnees.pieces_1 },
    { label: '2 pièces', valeur: logementDonnees.pieces_2 },
    { label: '3 pièces', valeur: logementDonnees.pieces_3 },
    { label: '4 pièces', valeur: logementDonnees.pieces_4 },
    { label: '5 pièces et +', valeur: logementDonnees.pieces_5p },
  ] : [];
  const occupationBars = logementDonnees ? [
    { label: 'Propriétaires', valeur: logementDonnees.proprietaires },
    { label: 'Locataires', valeur: logementDonnees.locataires_non_hlm },
    { label: 'Locataires HLM', valeur: logementDonnees.locataires_hlm },
    { label: 'Logé gratuitement', valeur: logementDonnees.loge_gratuit },
  ] : [];

  const sommaireItems = [
    'Le bien estimé',
    'Votre conseiller SAFTI',
    'Urbanisme et cadastre',
    'Connectivité internet (fibre / ADSL)',
    "Environnement et points d'intérêt",
    'Le marché immobilier local',
    'Notre étude de marché',
    'Informations légales',
  ];

  return (
    <div className="max-w-[1400px] mx-auto space-y-5">
      <style>{`
        .print-only { display: none; }
        .pdf-capture.estim-print { box-shadow: none !important; border: none !important; border-radius: 0 !important; }
        html:has(.pdf-capture) { font-size: 78%; }
        .pdf-capture .no-print { display: none !important; }
        .pdf-capture .print-only { display: block !important; }
        .pdf-capture, .pdf-capture * { -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
        .pdf-capture .print-block { overflow: visible !important; border-radius: 0 !important; margin-top: 6mm; }
      `}</style>

      {view === 'liste' && <StatistiquesAvis />}
      {view === 'liste' && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-1.5">🏷️ Estimation</h2>
              <p className="text-sm text-gray-500 mt-1 max-w-xl">
                Crée des dossiers d'estimation immobilière à partir de comparables DVF réels, ajuste les critères
                du bien, puis exporte un PDF prêt à envoyer au client.
              </p>
            </div>
            <button
              onClick={nouveauDossier}
              className="px-4 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-sm font-semibold rounded-xl hover:from-violet-700 hover:to-indigo-700 transition-all shadow-sm whitespace-nowrap"
            >
              + Nouveau dossier
            </button>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {loadingDossiers ? (
              <p className="text-sm text-gray-500 p-6">Chargement...</p>
            ) : dossiers.length === 0 ? (
              <p className="text-sm text-gray-500 p-6">Aucun dossier pour l'instant — crée ton premier dossier d'estimation.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-400 uppercase border-b border-gray-100">
                    <th className="px-4 py-3 font-medium">Adresse</th>
                    <th className="px-4 py-3 font-medium">Client</th>
                    <th className="px-4 py-3 font-medium">Motif</th>
                    <th className="px-4 py-3 font-medium">Bien</th>
                    <th className="px-4 py-3 font-medium">Estimation</th>
                    <th className="px-4 py-3 font-medium">Étape</th>
                    <th className="px-4 py-3 font-medium">Statut</th>
                    <th className="px-4 py-3 font-medium">Mis à jour</th>
                    <th className="px-4 py-3 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {dossiers.map(d => (
                    <tr key={d.id} className="border-b border-gray-50 last:border-b-0 hover:bg-gray-50/60 cursor-pointer" onClick={() => ouvrirDossier(d.id)}>
                      <td className="px-4 py-3 font-medium text-gray-900">{d.adresse_label}</td>
                      <td className="px-4 py-3 text-gray-600">{d.client_nom || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{MOTIF_LABELS[d.motif] || d.motif}</td>
                      <td className="px-4 py-3 text-gray-600">{TYPE_BIEN_LABELS[d.type_bien] || d.type_bien} · {d.surface_habitable} m²</td>
                      <td className="px-4 py-3 font-semibold text-violet-700">{formatEUR(d.montant_final ?? d.prix_retenu)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-lg border whitespace-nowrap ${ETAT_AVANCEMENT_COLORS[d.etat_avancement] || ETAT_AVANCEMENT_COLORS.a_contacter}`}>
                          {ETAT_AVANCEMENT_LABELS[d.etat_avancement] || d.etat_avancement}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-lg border ${d.statut === 'finalise' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                          {STATUT_LABELS[d.statut] || d.statut}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{formatDateFr(d.updated_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(d.id); }}
                          className="text-xs text-red-500 hover:text-red-700"
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {view === 'form' && (
        <div className="grid lg:grid-cols-[1fr_420px] gap-5 items-start">
          {/* ── Colonne gauche : formulaire ── */}
          <div className="no-print min-w-0 space-y-4">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="flex items-center justify-between p-4 pb-0">
                <button onClick={() => setView('liste')} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
                  ← Retour à la liste
                </button>
                {currentId && (
                  <button onClick={() => setConfirmDeleteId(currentId)} className="text-xs text-red-500 hover:text-red-700">
                    Supprimer ce dossier
                  </button>
                )}
              </div>
              <div className="flex gap-1 px-4 pt-3 overflow-x-auto border-b border-gray-100">
                {FORM_TABS.map(t => (
                  <button
                    key={t.key}
                    onClick={() => ouvrirOnglet(t.key)}
                    className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${formTab === t.key ? 'border-violet-600 text-violet-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="p-6">
                {formTab === 'client' && (
                  <>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Motif de l'estimation</h3>
                    <select
                      value={draft.motif}
                      onChange={e => setDraft(d => ({ ...d, motif: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-violet-200"
                    >
                      {MOTIF_ORDER.map(m => <option key={m} value={m}>{MOTIF_LABELS[m]}</option>)}
                    </select>

                    <h3 className="text-sm font-semibold text-gray-900 mb-3">État d'avancement</h3>
                    <select
                      value={draft.etatAvancement}
                      onChange={e => setDraft(d => ({ ...d, etatAvancement: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-violet-200"
                    >
                      {ETAT_AVANCEMENT_ORDER.map(k => <option key={k} value={k}>{ETAT_AVANCEMENT_LABELS[k]}</option>)}
                    </select>

                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Demandeur</h3>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <input
                        type="text" placeholder="Nom du demandeur" value={draft.clientNom}
                        onChange={e => setDraft(d => ({ ...d, clientNom: e.target.value }))}
                        className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                      />
                      <input
                        type="tel" placeholder="Téléphone" value={draft.demandeurTelephone}
                        onChange={e => setDraft(d => ({ ...d, demandeurTelephone: e.target.value }))}
                        className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                      />
                    </div>
                    <div className="mb-3">
                      <input
                        type="email" placeholder="Email du demandeur" value={draft.clientEmail}
                        onChange={e => setDraft(d => ({ ...d, clientEmail: e.target.value }))}
                        onBlur={() => setEmailTouched(true)}
                        className={`w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 ${emailTouched && !isEmailValide(draft.clientEmail) ? 'border-red-300 focus:ring-red-200' : 'border-gray-200 focus:ring-violet-200'}`}
                      />
                      {emailTouched && !isEmailValide(draft.clientEmail) && (
                        <p className="text-xs text-red-500 mt-1">Adresse email invalide</p>
                      )}
                    </div>
                    <input
                      type="text" placeholder="Adresse du demandeur (si différente du bien)" value={draft.demandeurAdresse}
                      onChange={e => setDraft(d => ({ ...d, demandeurAdresse: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-violet-200"
                    />

                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Propriétaire</h3>
                    <p className="text-xs text-gray-500 mb-2">Le demandeur est-il aussi le propriétaire du bien ?</p>
                    <div className="flex gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => setDraft(d => ({ ...d, proprietaireEstDemandeur: true }))}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-lg border ${draft.proprietaireEstDemandeur ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600 border-gray-200'}`}
                      >
                        Oui
                      </button>
                      <button
                        type="button"
                        onClick={() => setDraft(d => ({ ...d, proprietaireEstDemandeur: false }))}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-lg border ${!draft.proprietaireEstDemandeur ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600 border-gray-200'}`}
                      >
                        Non
                      </button>
                    </div>
                    {!draft.proprietaireEstDemandeur && (
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text" placeholder="Nom du propriétaire" value={draft.proprietaireNom}
                          onChange={e => setDraft(d => ({ ...d, proprietaireNom: e.target.value }))}
                          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                        />
                        <input
                          type="tel" placeholder="Téléphone" value={draft.proprietaireTelephone}
                          onChange={e => setDraft(d => ({ ...d, proprietaireTelephone: e.target.value }))}
                          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                        />
                        <input
                          type="email" placeholder="Email du propriétaire" value={draft.proprietaireEmail}
                          onChange={e => setDraft(d => ({ ...d, proprietaireEmail: e.target.value }))}
                          className="col-span-2 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                        />
                      </div>
                    )}
                  </>
                )}

                {formTab === 'bien' && (
                  <>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Adresse du bien</h3>
                    <div className="relative mb-4">
                      <input
                        type="text"
                        value={addressInput}
                        onChange={e => setAddressInput(e.target.value)}
                        onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                        placeholder="Ex: 12 rue de la République, 38140 Rives"
                        autoComplete="off"
                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                      />
                      {showSuggestions && suggestions.length > 0 && (
                        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                          {suggestions.map((s, i) => (
                            <button
                              type="button" key={i} onMouseDown={() => selectSuggestion(s)}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-violet-50 border-b border-gray-50 last:border-b-0"
                            >
                              {s.label}
                            </button>
                          ))}
                        </div>
                      )}
                      {draft.adresseLabel && draft.lon != null && (
                        <p className="text-xs text-emerald-600 mt-1">✓ Adresse géolocalisée</p>
                      )}
                    </div>

                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Caractéristiques du bien</h3>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <select
                        value={draft.typeBien}
                        onChange={e => setDraft(d => ({ ...d, typeBien: e.target.value }))}
                        className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                      >
                        {TYPE_BIEN_ORDER.map(t => <option key={t} value={t}>{TYPE_BIEN_LABELS[t]}</option>)}
                      </select>
                      <input
                        type="number"
                        placeholder={draft.typeBien === 'Terrain' ? 'Surface du terrain (m²)' : 'Surface habitable (m²)'}
                        value={draft.surfaceHabitable}
                        onChange={e => setDraft(d => ({ ...d, surfaceHabitable: e.target.value }))}
                        className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <input
                        type="number" placeholder="Nombre de pièces" value={draft.nbPieces}
                        onChange={e => setDraft(d => ({ ...d, nbPieces: e.target.value }))}
                        className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                      />
                      <input
                        type="number" placeholder="Année de construction" value={draft.anneeConstruction}
                        onChange={e => setDraft(d => ({ ...d, anneeConstruction: e.target.value }))}
                        className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                      />
                    </div>
                    <select
                      value={draft.etatGeneral}
                      onChange={e => setDraft(d => ({ ...d, etatGeneral: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-violet-200"
                    >
                      {Object.entries(ETAT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    {!TYPES_SANS_CRITERES_BATI.has(draft.typeBien) && (
                      <>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <select
                            value={draft.etage}
                            onChange={e => setDraft(d => ({ ...d, etage: e.target.value }))}
                            className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                          >
                            <option value="">Étage (si appartement)</option>
                            {Object.entries(ETAGE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                          <select
                            value={draft.exposition}
                            onChange={e => setDraft(d => ({ ...d, exposition: e.target.value }))}
                            className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                          >
                            <option value="">Exposition</option>
                            {Object.entries(EXPOSITION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                        </div>
                        <div className="grid grid-cols-2 gap-3 items-center">
                          <select
                            value={draft.dpeLettre}
                            onChange={e => setDraft(d => ({ ...d, dpeLettre: e.target.value }))}
                            className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                          >
                            <option value="">DPE — non renseigné</option>
                            {DPE_LETTRES.map(l => <option key={l} value={l}>{l}</option>)}
                            <option value={DPE_VIERGE}>Vierge (non soumis au DPE)</option>
                          </select>
                          <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox" checked={draft.travauxRecents}
                              onChange={e => setDraft(d => ({ ...d, travauxRecents: e.target.checked }))}
                              className="rounded border-gray-300"
                            />
                            Travaux récents
                          </label>
                        </div>
                      </>
                    )}
                  </>
                )}

                {formTab === 'caracteristiques' && (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-900">Profil qualitatif du bien</h3>
                      {resultat?.indiceQualitePct != null && (
                        <span className={`text-xs font-semibold px-2 py-1 rounded-lg border ${resultat.indiceQualitePct >= 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                          Indice de qualité : {resultat.indiceQualitePct > 0 ? '+' : ''}{resultat.indiceQualitePct}%
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mb-4">
                      Note chaque critère pour affiner l'estimation. Les catégories non notées apparaissent au niveau
                      neutre (référence à 60%) sur le graphique, sans impact sur le prix.
                    </p>
                    <div className="h-64 mb-4 -mx-2">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={radarData} outerRadius="75%">
                          <PolarGrid />
                          <PolarAngleAxis dataKey="categorie" tick={{ fontSize: 10 }} />
                          <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 9 }} />
                          <Radar name="Votre bien" dataKey="score" stroke="#7c3aed" fill="#7c3aed" fillOpacity={0.35} />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                    {!aCriteresRenseignes && (
                      <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-4">
                        Aucun critère noté pour l'instant — le graphique affiche une référence neutre par défaut.
                      </p>
                    )}
                    <div className="space-y-5 max-h-[520px] overflow-y-auto pr-1">
                      {CRITERES_CATEGORIES.map(cat => (
                        <div key={cat.key}>
                          <p className="text-xs font-bold uppercase tracking-wide text-violet-700 mb-2">{cat.label}</p>
                          {cat.criteres.map(crit => (
                            <CritereRow
                              key={crit.key}
                              label={crit.label}
                              options={crit.options}
                              value={draft.criteresQualitatifs[cat.key]?.[crit.key]}
                              onChange={v => updateCritere(cat.key, crit.key, v)}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {formTab === 'estimation' && (
                  <>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Comparables DVF</h3>
                    <button
                      onClick={rechercherComparables}
                      disabled={comparablesLoading}
                      className="w-full px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-xl hover:bg-gray-800 transition-all disabled:opacity-60 mb-2"
                    >
                      {comparablesLoading ? 'Recherche des ventes comparables...' : '🔍 Chercher les comparables DVF'}
                    </button>
                    {comparablesError && <p className="text-sm text-red-500 mb-3">❌ {comparablesError}</p>}

                    {comparables.length > 0 && (
                      <div className="mb-5">
                        <p className="text-xs text-gray-500 mb-2">
                          {comparablesRetenus.length} retenu(s) sur {comparables.length} — décoche celles qui ne sont pas pertinentes.
                        </p>
                        <div className="max-h-[320px] overflow-y-auto -mx-2 border border-gray-100 rounded-xl">
                          <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-white">
                              <tr className="text-left text-gray-400 uppercase border-b border-gray-100">
                                <th className="px-2 py-2 font-medium"></th>
                                <th className="px-2 py-2 font-medium">Adresse</th>
                                <th className="px-2 py-2 font-medium">Date</th>
                                <th className="px-2 py-2 font-medium">Surface</th>
                                <th className="px-2 py-2 font-medium">Prix</th>
                                <th className="px-2 py-2 font-medium">€/m²</th>
                                <th className="px-2 py-2 font-medium">Distance</th>
                              </tr>
                            </thead>
                            <tbody>
                              {comparables.map(c => (
                                <tr key={c.idMutation} className="border-b border-gray-50 last:border-b-0">
                                  <td className="px-2 py-2">
                                    <input type="checkbox" checked={selectedIds.has(c.idMutation)} onChange={() => toggleComparable(c.idMutation)} className="rounded border-gray-300" />
                                  </td>
                                  <td className="px-2 py-2 text-gray-700">{c.adresse || '—'}</td>
                                  <td className="px-2 py-2 text-gray-500">{formatDateFr(c.dateVente)}</td>
                                  <td className="px-2 py-2 text-gray-500">{c.surface} m²</td>
                                  <td className="px-2 py-2 text-gray-700">{formatEUR(c.prix)}</td>
                                  <td className="px-2 py-2 font-medium text-gray-900">{formatEUR(c.prixM2)}</td>
                                  <td className="px-2 py-2 text-gray-400">{(c.distanceM / 1000).toFixed(2)} km</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Méthodes de valorisation</h3>
                    <label className="flex items-center gap-2 text-sm text-gray-700 mb-2">
                      <input type="checkbox" checked={draft.methodeComparaisonActive} onChange={e => setDraft(d => ({ ...d, methodeComparaisonActive: e.target.checked }))} className="rounded border-gray-300" />
                      Comparaison DVF (comparables ci-dessus)
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 mb-2">
                      <input type="checkbox" checked={draft.methodeCapitalisationActive} onChange={e => setDraft(d => ({ ...d, methodeCapitalisationActive: e.target.checked }))} className="rounded border-gray-300" />
                      Capitalisation locative
                    </label>
                    {draft.methodeCapitalisationActive && (
                      <div className="grid grid-cols-2 gap-3 mb-4 pl-6">
                        <input
                          type="number" placeholder="Loyer mensuel estimé (€)" value={draft.loyerMensuelEstime}
                          onChange={e => setDraft(d => ({ ...d, loyerMensuelEstime: e.target.value }))}
                          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                        />
                        <input
                          type="number" placeholder="Taux de rendement (%)" value={draft.tauxRendementPct}
                          onChange={e => setDraft(d => ({ ...d, tauxRendementPct: e.target.value }))}
                          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                        />
                        {resultat?.prixCapitalisation != null && (
                          <p className="col-span-2 text-xs text-gray-500">Estimation par capitalisation : <span className="font-semibold text-gray-700">{formatEUR(resultat.prixCapitalisation)}</span></p>
                        )}
                      </div>
                    )}

                    <h3 className="text-sm font-semibold text-gray-900 mb-3 mt-2">Ajustements manuels</h3>
                    <div className="space-y-2 mb-4">
                      {AJUSTEMENT_ORDER.map(key => (
                        <div key={key} className="flex items-center gap-2">
                          <p className="text-xs text-gray-600 flex-1">{AJUSTEMENT_LABELS[key]}</p>
                          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                            <button
                              type="button"
                              onClick={() => updateAjustement(key, { mode: 'euro' })}
                              className={`px-2 py-1 text-xs ${draft.ajustements[key].mode === 'euro' ? 'bg-violet-600 text-white' : 'bg-white text-gray-500'}`}
                            >€</button>
                            <button
                              type="button"
                              onClick={() => updateAjustement(key, { mode: 'pct' })}
                              className={`px-2 py-1 text-xs border-l border-gray-200 ${draft.ajustements[key].mode === 'pct' ? 'bg-violet-600 text-white' : 'bg-white text-gray-500'}`}
                            >%</button>
                          </div>
                          <input
                            type="number" value={draft.ajustements[key].valeur} placeholder="0"
                            onChange={e => updateAjustement(key, { valeur: e.target.value })}
                            className="w-24 border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-violet-200"
                          />
                        </div>
                      ))}
                    </div>

                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Honoraires & affichage</h3>
                    <div className="grid grid-cols-2 gap-3 mb-3 items-center">
                      <input
                        type="number" placeholder="Honoraires (%)" value={draft.honorairesPct}
                        onChange={e => setDraft(d => ({ ...d, honorairesPct: e.target.value }))}
                        className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setDraft(d => ({ ...d, affichageNetVendeur: true }))}
                          className={`flex-1 px-3 py-2 text-xs font-semibold rounded-lg border ${draft.affichageNetVendeur ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600 border-gray-200'}`}
                        >
                          Net vendeur
                        </button>
                        <button
                          type="button"
                          onClick={() => setDraft(d => ({ ...d, affichageNetVendeur: false }))}
                          className={`flex-1 px-3 py-2 text-xs font-semibold rounded-lg border ${!draft.affichageNetVendeur ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-600 border-gray-200'}`}
                        >
                          Prix FAI
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mb-3 mt-4">
                      <h3 className="text-sm font-semibold text-gray-900">Analyse de l'expert (IA)</h3>
                      <button
                        onClick={genererAnalyseIa}
                        disabled={analyseIaLoading}
                        className="text-xs px-3 py-1.5 bg-violet-600 text-white font-semibold rounded-lg hover:bg-violet-700 disabled:opacity-60"
                      >
                        {analyseIaLoading ? 'Génération...' : analyseIa ? '✨ Régénérer' : '✨ Générer (IA)'}
                      </button>
                    </div>
                    {analyseIaError && <p className="text-sm text-red-500 mb-2">❌ {analyseIaError}</p>}
                    <textarea
                      value={analyseIa}
                      onChange={e => setAnalyseIa(e.target.value)}
                      placeholder="Génère un paragraphe d'analyse à partir des comparables, critères et environnement du dossier — modifiable ensuite librement."
                      rows={5}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200 resize-none"
                    />
                  </>
                )}

                {formTab === 'environnement' && (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-900">Points d'intérêt à proximité</h3>
                      <button onClick={() => chargerEnvironnement()} disabled={environnementLoading || draft.lon == null} className="text-xs text-violet-600 hover:text-violet-800 disabled:opacity-40">
                        {environnementLoading ? 'Chargement...' : '↻ Actualiser'}
                      </button>
                    </div>
                    {draft.lon == null ? (
                      <p className="text-sm text-gray-400">Renseigne d'abord l'adresse du bien (onglet "Bien immobilier").</p>
                    ) : environnementLoading && !environnement ? (
                      <p className="text-sm text-gray-500">Chargement des points d'intérêt...</p>
                    ) : environnement ? (
                      <div className="space-y-4">
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wide text-violet-700 mb-2">Administration</p>
                          {environnement.poi.administration.length === 0 ? <p className="text-xs text-gray-400">Aucun résultat proche.</p> : (
                            <ul className="text-sm text-gray-700 space-y-1">
                              {environnement.poi.administration.map((p, i) => (
                                <li key={i} className="flex justify-between"><span>{p.nom}</span><span className="text-gray-400 text-xs">{(p.distanceM / 1000).toFixed(1)} km</span></li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wide text-violet-700 mb-2">Enseignement</p>
                          {(['maternelle', 'primaire', 'college', 'lycee'] as const).map(niveau => (
                            <div key={niveau} className="flex justify-between text-sm text-gray-700">
                              <span className="capitalize">{niveau}</span>
                              <span className="text-gray-400 text-xs">{environnement.ecoles?.[niveau] ? `${environnement.ecoles[niveau]!.nom} — ${formatTrajetCourt(environnement.ecoles[niveau])}` : '—'}</span>
                            </div>
                          ))}
                        </div>
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wide text-violet-700 mb-2">Transports</p>
                          {environnement.poi.transports.length === 0 ? <p className="text-xs text-gray-400">Aucun arrêt proche recensé.</p> : (
                            <ul className="text-sm text-gray-700 space-y-1">
                              {environnement.poi.transports.map((p, i) => (
                                <li key={i} className="flex justify-between"><span>{p.type} {p.nom !== p.type ? `— ${p.nom}` : ''}</span><span className="text-gray-400 text-xs">{p.distanceM} m</span></li>
                              ))}
                            </ul>
                          )}
                          {environnement.gare?.gare && (
                            <div className="flex justify-between text-sm text-gray-700 mt-1">
                              <span>Gare — {environnement.gare.gare.nom}</span>
                              <span className="text-gray-400 text-xs">{formatTrajetCourt(environnement.gare.gare)}</span>
                            </div>
                          )}
                          {environnement.autoroute?.sortie && (
                            <div className="flex justify-between text-sm text-gray-700 mt-1">
                              <span>Sortie autoroute — {environnement.autoroute.sortie.nom}</span>
                              <span className="text-gray-400 text-xs">{formatTrajetCourt(environnement.autoroute.sortie)}</span>
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wide text-violet-700 mb-2">Santé</p>
                          {environnement.poi.sante.length === 0 ? <p className="text-xs text-gray-400">Aucun résultat proche.</p> : (
                            <ul className="text-sm text-gray-700 space-y-1">
                              {environnement.poi.sante.map((p, i) => (
                                <li key={i} className="flex justify-between"><span>{p.nom}</span><span className="text-gray-400 text-xs">{(p.distanceM / 1000).toFixed(1)} km</span></li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400">Clique sur "Actualiser" pour charger les points d'intérêt.</p>
                    )}
                  </>
                )}

                {formTab === 'suivi' && (
                  <>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Suivi commercial</h3>
                    {!currentId ? (
                      <p className="text-sm text-gray-400">Enregistre le dossier pour activer le suivi commercial.</p>
                    ) : (
                      <>
                        <div className="flex gap-2 mb-4">
                          <input
                            type="text" placeholder="Ajouter une note..." value={noteInput}
                            onChange={e => setNoteInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') ajouterNote(); }}
                            className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                          />
                          <button
                            onClick={ajouterNote}
                            disabled={addingNote || !noteInput.trim()}
                            className="px-4 py-2 bg-violet-600 text-white text-sm font-semibold rounded-xl hover:bg-violet-700 disabled:opacity-50"
                          >
                            Ajouter
                          </button>
                        </div>
                        {activitesLoading ? (
                          <p className="text-sm text-gray-500">Chargement...</p>
                        ) : activites.length === 0 ? (
                          <p className="text-sm text-gray-400">Aucune activité pour l'instant.</p>
                        ) : (
                          <ul className="space-y-3">
                            {activites.map(a => (
                              <li key={a.id} className="flex gap-2 text-sm">
                                <span>{ACTIVITE_ICONS[a.type] || '•'}</span>
                                <div>
                                  <p className="text-gray-700">{a.description}</p>
                                  <p className="text-xs text-gray-400">{formatDateHeureFr(a.created_at)}</p>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            {currentId && (
              <AvisValeurCarte dossierId={currentId} clientNom={draft.clientNom} clientEmail={draft.clientEmail} clientTelephone={draft.demandeurTelephone || draft.proprietaireTelephone} adresse={draft.adresseLabel} />
            )}

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Notes internes</h3>
              <textarea
                value={draft.notes}
                onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))}
                placeholder="Notes internes (non visibles par le client si tu ne les mentionnes pas dans le PDF)"
                rows={3}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200 resize-none"
              />
              <div className="flex items-center gap-3 mt-4">
                <select
                  value={statut}
                  onChange={e => setStatut(e.target.value)}
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                >
                  <option value="brouillon">Brouillon</option>
                  <option value="finalise">Finalisé</option>
                </select>
                <button
                  onClick={suivantOuEnregistrer}
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-sm font-semibold rounded-xl hover:from-violet-700 hover:to-indigo-700 transition-all shadow-sm disabled:opacity-60"
                >
                  {saving
                    ? 'Enregistrement...'
                    : !peutEnregistrer && !dernierOnglet
                      ? `Suivant : ${FORM_TABS[formTabIndex + 1].label} →`
                      : currentId ? 'Mettre à jour le dossier' : 'Enregistrer le dossier'}
                </button>
              </div>
              {saveMsg && <p className="text-sm mt-2">{saveMsg}</p>}
              {!peutEnregistrer && !saveMsg && (
                <p className="text-xs text-gray-400 mt-2">Adresse et surface requises avant de pouvoir enregistrer.</p>
              )}
            </div>
          </div>

          {/* ── Colonne droite : aperçu / résultat / export PDF ── */}
          <div className="space-y-4 lg:sticky lg:top-4">
            <div className="no-print bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex gap-2">
              <button
                onClick={handleTelecharger}
                disabled={rapportEnCoursDeChargement}
                className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-200 transition-all disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
              >
                {rapportEnCoursDeChargement ? (
                  <><span className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /> Préparation du rapport...</>
                ) : '⬇️ Télécharger le PDF'}
              </button>
              <button
                onClick={handlePartager}
                disabled={sharing || rapportEnCoursDeChargement}
                className="flex-1 px-3 py-2 bg-violet-600 text-white text-sm font-semibold rounded-xl hover:bg-violet-700 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {sharing ? 'Préparation...' : rapportEnCoursDeChargement ? 'Préparation du rapport...' : '📤 Partager'}
              </button>
            </div>
            {rapportEnCoursDeChargement && (
              <p className="no-print text-xs text-gray-400 -mt-2 px-1">
                Chargement des données du rapport (urbanisme, environnement, comparables)... le bouton s'active automatiquement une fois prêt.
              </p>
            )}

            <div className="no-print bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
              <button
                onClick={envoyerParEmail}
                disabled={rapportEnCoursDeChargement || envoiEmailLoading || !draft.clientEmail}
                title={!draft.clientEmail ? 'Renseigne l\'email du demandeur (onglet Demandeur) pour activer l\'envoi' : undefined}
                className="w-full px-3 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-xl hover:bg-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
              >
                {envoiEmailLoading ? (
                  <><span className="w-3.5 h-3.5 border-2 border-white/60 border-t-transparent rounded-full animate-spin" /> Envoi en cours...</>
                ) : `✉️ Envoyer au client${draft.clientEmail ? ` (${draft.clientEmail})` : ''}`}
              </button>
              {envoiEmailMsg && <p className="text-xs mt-2">{envoiEmailMsg}</p>}
            </div>

            <div className="no-print flex items-center justify-between gap-3 bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-center gap-2">
                {agentProfile?.photoUrl && <img src={agentProfile.photoUrl} alt="" className="w-10 h-10 rounded-full object-cover" />}
                <div>
                  <p className="text-sm font-semibold text-gray-900">{agentProfile?.name || 'Estimation immobilière'}</p>
                  {agentProfile?.activite && <p className="text-xs text-gray-500">{agentProfile.activite}{agentProfile.ville ? ` — ${agentProfile.ville}` : ''}</p>}
                </div>
              </div>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-violet-50 text-violet-700 border border-violet-200 whitespace-nowrap">
                {MOTIF_LABELS[draft.motif]}
              </span>
            </div>

            <div ref={printRef} className="estim-print bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              {/* ── Page 1 : Couverture ──
                  Ratio fixé à celui d'une page A4 (210/297) : la couverture
                  occupe ainsi TOUJOURS exactement une page PDF, quelle que
                  soit la largeur d'affichage — elle ne peut plus se retrouver
                  coupée en deux par le découpage automatique en pages. */}
              <div className="print-block rounded-2xl overflow-hidden mb-4 flex flex-col" style={{ border: `2px solid ${PRINT_BRAND}`, aspectRatio: '210 / 297' }}>
                <div className="px-6 py-6 text-center text-white flex-shrink-0" style={{ backgroundColor: PRINT_BRAND }}>
                  {agentProfile?.entreprise && (
                    <p className="text-xs font-semibold tracking-widest uppercase opacity-90">{agentProfile.entreprise}</p>
                  )}
                  <h1 className="text-2xl font-bold tracking-wide mt-2">{COVER_TITLES[draft.motif] || 'ESTIMATION IMMOBILIÈRE'}</h1>
                </div>
                <div className="relative bg-gray-100 flex-1 min-h-0">
                  {satView && (
                    <>
                      <img
                        src={`${CADASTRE_API}/satellite?bbox=${encodeURIComponent(satView.bbox)}&size=${satView.size}`}
                        crossOrigin="anonymous"
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      {satView.pathD && (
                        <svg viewBox={`0 0 ${satView.size} ${satView.size}`} className="absolute inset-0 w-full h-full">
                          <path d={satView.pathD} fill="rgba(242,125,73,0.28)" stroke={PRINT_BRAND} strokeWidth={5} fillRule="evenodd" />
                        </svg>
                      )}
                    </>
                  )}
                </div>
                <div className="px-6 py-5 text-center text-white flex-shrink-0" style={{ backgroundColor: PRINT_BRAND }}>
                  <p className="text-sm">
                    Évaluation du {generatedAt.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                  {draft.clientNom && <p className="text-sm mt-1">À la demande de {draft.clientNom}</p>}
                  <p className="font-semibold mt-2">{draft.adresseLabel || 'Adresse non renseignée'}</p>
                </div>
              </div>

              {/* ── Page 2 : Sommaire ── */}
              <PageBreak />
              <PageHeader title={docTitle} />
              <PB title="Sommaire">
                <div className="divide-y divide-gray-50">
                  {sommaireItems.map((item, i) => (
                    <SommaireLigne key={item} label={item} numero={i + 1} />
                  ))}
                </div>
              </PB>

              {/* ── Page 3 : Le bien estimé ── */}
              <PageBreak />
              <PageHeader title={docTitle} />
              <PB icon="🏠" title="Le bien estimé">
                <p className="font-semibold text-gray-900 text-sm mb-3">{draft.adresseLabel || 'Adresse non renseignée'}</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div><span className="text-gray-400 text-xs block">Type de bien</span>{TYPE_BIEN_LABELS[draft.typeBien] || draft.typeBien}</div>
                  <div><span className="text-gray-400 text-xs block">Surface habitable</span>{draft.surfaceHabitable ? `${draft.surfaceHabitable} m²` : '—'}</div>
                  {draft.nbPieces && <div><span className="text-gray-400 text-xs block">Nombre de pièces</span>{draft.nbPieces}</div>}
                  {draft.anneeConstruction && <div><span className="text-gray-400 text-xs block">Année de construction</span>{draft.anneeConstruction}</div>}
                  <div><span className="text-gray-400 text-xs block">État général</span>{ETAT_LABELS[draft.etatGeneral]}</div>
                  {draft.etage && <div><span className="text-gray-400 text-xs block">Étage</span>{ETAGE_LABELS[draft.etage]}</div>}
                  {draft.exposition && <div><span className="text-gray-400 text-xs block">Exposition</span>{EXPOSITION_LABELS[draft.exposition]}</div>}
                  <div><span className="text-gray-400 text-xs block">Diagnostic énergétique</span>{draft.dpeLettre ? (draft.dpeLettre === DPE_VIERGE ? 'Vierge' : `DPE ${draft.dpeLettre}`) : 'Non renseigné'}</div>
                  {draft.travauxRecents && <div><span className="text-gray-400 text-xs block">Travaux</span>Travaux récents effectués</div>}
                  {urbanisme?.parcelle?.contenanceM2 != null && (
                    <div><span className="text-gray-400 text-xs block">Surface du terrain</span>{Math.round(urbanisme.parcelle.contenanceM2).toLocaleString('fr-FR')} m²</div>
                  )}
                  {batimentPrincipal?.nombreEtages != null && (
                    <div><span className="text-gray-400 text-xs block">Nombre de niveaux</span>{batimentPrincipal.nombreEtages}</div>
                  )}
                  {batimentPrincipal?.nombreLogements != null && batimentPrincipal.nombreLogements > 1 && (
                    <div><span className="text-gray-400 text-xs block">Nombre de logements</span>{batimentPrincipal.nombreLogements}</div>
                  )}
                  {formatMateriauMurs(batimentPrincipal?.materiauxMurs) && (
                    <div><span className="text-gray-400 text-xs block">Matériaux (murs)</span>{formatMateriauMurs(batimentPrincipal?.materiauxMurs)}</div>
                  )}
                  {formatMateriauToiture(batimentPrincipal?.materiauxToiture) && (
                    <div><span className="text-gray-400 text-xs block">Matériaux (toiture)</span>{formatMateriauToiture(batimentPrincipal?.materiauxToiture)}</div>
                  )}
                  {batimentPrincipal?.empriseSolM2 != null && (
                    <div><span className="text-gray-400 text-xs block">Emprise au sol</span>{Math.round(batimentPrincipal.empriseSolM2).toLocaleString('fr-FR')} m²</div>
                  )}
                </div>
                {!urbanisme && (
                  <p className="text-xs text-gray-400 mt-3 pt-2 border-t border-gray-100">Chargement des données cadastrales et bâtiment (BDNB)...</p>
                )}
                {(draft.clientNom || draft.clientEmail || (!draft.proprietaireEstDemandeur && draft.proprietaireNom)) && (
                  <div className="text-xs text-gray-500 pt-2 border-t border-gray-100 mt-3 space-y-0.5">
                    {(draft.clientNom || draft.clientEmail) && (
                      <p>Demandeur : {draft.clientNom || '—'} {draft.clientEmail ? `(${draft.clientEmail})` : ''}{draft.demandeurTelephone ? ` · ${draft.demandeurTelephone}` : ''}</p>
                    )}
                    {!draft.proprietaireEstDemandeur && draft.proprietaireNom && (
                      <p>Propriétaire : {draft.proprietaireNom}{draft.proprietaireTelephone ? ` · ${draft.proprietaireTelephone}` : ''}</p>
                    )}
                  </div>
                )}
              </PB>

              {/* ── Page 4 : Votre conseiller SAFTI (page dédiée, comme
                  "Présentation de l'agence" chez le concurrent — chez SAFTI
                  le conseiller EST l'interlocuteur, pas une agence distincte) ── */}
              <PageBreak />
              <PageHeader title={docTitle} />
              <PB icon="🧑‍💼" title="Votre conseiller SAFTI">
                <div className="flex items-center gap-4">
                  {agentProfile?.photoUrl && (
                    <img src={agentProfile.photoUrl} crossOrigin="anonymous" alt="" className="w-20 h-20 rounded-full object-cover flex-shrink-0" />
                  )}
                  <div className="text-sm">
                    <p className="font-semibold text-gray-900 text-base">{agentProfile?.name || agentProfile?.entreprise || 'SAFTI'}</p>
                    {agentProfile?.activite && <p className="text-gray-500">{agentProfile.activite}{agentProfile.ville ? ` — ${agentProfile.ville}` : ''}</p>}
                    {agentProfile?.telephone && <p className="text-gray-500 mt-1">Tél : {agentProfile.telephone}</p>}
                    {agentProfile?.email && <p className="text-gray-500">{agentProfile.email}</p>}
                    {agentProfile?.siteWeb && <p className="text-gray-500">{agentProfile.siteWeb}</p>}
                  </div>
                </div>
              </PB>

              {/* ── Page 5 : Urbanisme & cadastre ── */}
              <PageBreak />
              <PageHeader title={docTitle} />
              <PB icon="🗺️" title="Urbanisme et cadastre">
                {satView && (
                  <div className="relative rounded-lg overflow-hidden mb-3 bg-gray-100" style={{ aspectRatio: '1 / 1' }}>
                    <img
                      src={`${CADASTRE_API}/satellite?bbox=${encodeURIComponent(satView.bbox)}&size=${satView.size}`}
                      crossOrigin="anonymous"
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    {satView.pathD && (
                      <svg viewBox={`0 0 ${satView.size} ${satView.size}`} className="absolute inset-0 w-full h-full">
                        <path d={satView.pathD} fill="rgba(242,125,73,0.28)" stroke={PRINT_BRAND} strokeWidth={5} fillRule="evenodd" />
                      </svg>
                    )}
                  </div>
                )}
                {urbanisme?.parcelle ? (
                  <p className="text-sm text-gray-700">
                    Référence cadastrale : section {urbanisme.parcelle.section} n°{urbanisme.parcelle.numero} — {Math.round(urbanisme.parcelle.contenanceM2).toLocaleString('fr-FR')} m² — {urbanisme.parcelle.nomCom}
                  </p>
                ) : (
                  <p className="text-sm text-gray-400">Référence cadastrale non disponible.</p>
                )}
                {urbanisme?.zonage ? (
                  <p className="text-sm text-gray-700 mt-2">
                    Zone d'urbanisme (PLU) : <span className="font-semibold">{urbanisme.zonage.libelle}</span> — {urbanisme.zonage.libelong}
                    {urbanisme.zonage.ambigu && <span className="text-amber-600"> (zone à confirmer, plusieurs zonages détectés à proximité)</span>}
                  </p>
                ) : (
                  <p className="text-sm text-gray-400 mt-2">Zonage PLU non disponible pour cette adresse.</p>
                )}
              </PB>

              {/* ── Page 6 : Connectivité internet (Arcep) ── */}
              <PageBreak />
              <PageHeader title={docTitle} />
              <PB icon="🌐" title="Connectivité internet (fibre / ADSL)">
                {urbanisme?.eligibiliteInternet?.status === 'ready' && urbanisme.eligibiliteInternet.technologies && urbanisme.eligibiliteInternet.technologies.length > 0 ? (
                  <div className="space-y-3">
                    {(() => {
                      const techs = urbanisme.eligibiliteInternet!.technologies!;
                      const technos = techs.map(t => t.technologie);
                      const adslOk = technos.includes('CU');
                      const fibreOk = technos.includes('FO');
                      const pill = (label: string, ok: boolean) => (
                        <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-lg border ${ok ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                          {label} : {ok ? 'OUI' : 'NON'}
                        </span>
                      );

                      // Regroupé par TECHNOLOGIE (ligne) plutôt que par opérateur : le
                      // nombre d'opérateurs varie beaucoup d'une adresse à l'autre (2 en
                      // ville, 7-8 en zone couverte par plusieurs RIP régionaux), alors
                      // que le nombre de technologies est toujours petit et fixe — ça
                      // reste lisible quel que soit le nombre d'opérateurs, et se
                      // scanne plus facilement ("la fibre est dispo ? qui la propose ?")
                      // que l'inverse. Texte simple (pas de pavé de couleur) pour rester
                      // net, à l'image d'une fiche de renseignement d'opérateur.
                      const TECHNO_PRIORITE = ['FO', 'CA', '4GF', 'CU', 'THD_RADIO', 'SAT'];
                      const technosGroupees = TECHNO_PRIORITE
                        .map(code => ({ code, offres: techs.filter(t => t.technologie === code) }))
                        .filter(g => g.offres.length > 0);

                      return (
                        <>
                          <div className="flex flex-wrap gap-2 mb-4">{pill('ADSL', adslOk)}{pill('Fibre', fibreOk)}</div>
                          <div>
                            {technosGroupees.map(({ code, offres }) => (
                              <div key={code} className="flex items-start gap-3 py-2.5 border-b border-gray-50 last:border-b-0">
                                <span className="w-32 flex-shrink-0 text-sm font-semibold text-gray-800">
                                  {TECHNO_ICONS[code] || ''} {TECHNO_LABELS[code] || code}
                                </span>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 flex-1 pt-0.5">
                                  {offres
                                    .slice()
                                    .sort((a, b) => labelOperateur(a.operateur).localeCompare(labelOperateur(b.operateur)))
                                    .map((t, i) => {
                                      const debit = formatDebit(t.debitDescendant);
                                      return (
                                        <span key={i} className="text-xs whitespace-nowrap">
                                          <span className="font-medium text-gray-700">{labelOperateur(t.operateur)}</span>
                                          <span className="text-gray-400"> · {debit || 'éligible'}</span>
                                        </span>
                                      );
                                    })}
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      );
                    })()}
                    <p className="text-xs text-gray-400">
                      Donnée officielle Arcep (Ma connexion internet), immeuble le plus proche{urbanisme.eligibiliteInternet.distanceM != null ? ` (${urbanisme.eligibiliteInternet.distanceM} m)` : ''}.
                    </p>
                  </div>
                ) : urbanisme?.eligibiliteInternet?.status === 'importing' ? (
                  <p className="text-sm text-gray-400">Données Arcep en cours de chargement pour ce département (réessayer un peu plus tard).</p>
                ) : urbanisme?.eligibiliteInternet?.status === 'ready' ? (
                  <p className="text-sm text-gray-400">Aucun immeuble référencé Arcep à proximité immédiate de cette adresse.</p>
                ) : (
                  <p className="text-sm text-gray-400">Donnée de connectivité non disponible pour cette adresse.</p>
                )}
              </PB>

              {/* ── Page 7 : Environnement ── */}
              <PageBreak />
              <PageHeader title={docTitle} />
              <PB icon="📍" title="Environnement et points d'intérêt">
                {environnement ? (() => {
                  const ecolesListe = [
                    environnement.ecoles?.maternelle && { ...environnement.ecoles.maternelle, label: 'École maternelle' },
                    environnement.ecoles?.primaire && { ...environnement.ecoles.primaire, label: 'École primaire' },
                    environnement.ecoles?.college && { ...environnement.ecoles.college, label: 'Collège' },
                    environnement.ecoles?.lycee && { ...environnement.ecoles.lycee, label: 'Lycée' },
                  ].filter(Boolean) as Array<{ nom: string; label: string } & Record<string, any>>;
                  const transportsListe = [
                    ...(environnement.poi.transports || []).slice(0, 3),
                    environnement.gare?.gare && { nom: `Gare de ${environnement.gare.gare.nom}`, distanceM: null, _trajet: environnement.gare.gare },
                    environnement.autoroute?.sortie && { nom: `Sortie autoroute : ${environnement.autoroute.sortie.nom}`, distanceM: null, _trajet: environnement.autoroute.sortie },
                  ].filter(Boolean) as Array<{ nom: string; distanceM: number | null; _trajet?: any }>;
                  const administrationListe = (environnement.poi.administration || []).slice(0, 3);
                  const santeListe = (environnement.poi.sante || []).slice(0, 3);
                  const totalItems = ecolesListe.length + transportsListe.length + administrationListe.length + santeListe.length;

                  const Categorie = ({ icon, titre, children }: { icon: string; titre: string; children: React.ReactNode }) => (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5">{icon} {titre}</p>
                      <ul className="text-xs text-gray-600 space-y-1">{children}</ul>
                    </div>
                  );

                  return totalItems > 0 ? (
                    <>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                        <Categorie icon="🏛️" titre="Administration">
                          {administrationListe.length ? administrationListe.map((p, i) => (
                            <li key={i}>{p.nom} <span className="text-gray-400">— {(p.distanceM / 1000).toFixed(1)} km</span></li>
                          )) : <li className="text-gray-400">Aucun résultat proche</li>}
                        </Categorie>
                        <Categorie icon="🎒" titre="Enseignement">
                          {ecolesListe.length ? ecolesListe.map((e, i) => (
                            <li key={i}>{e.label} : {e.nom} <span className="text-gray-400">— {formatTrajetCourt(e as any)}</span></li>
                          )) : <li className="text-gray-400">Aucun résultat proche</li>}
                        </Categorie>
                        <Categorie icon="🚌" titre="Transports">
                          {transportsListe.length ? transportsListe.map((t, i) => (
                            <li key={i}>{t.nom} {t._trajet ? <span className="text-gray-400">— {formatTrajetCourt(t._trajet)}</span> : t.distanceM != null ? <span className="text-gray-400">— {(t.distanceM / 1000).toFixed(1)} km</span> : null}</li>
                          )) : <li className="text-gray-400">Aucun résultat proche</li>}
                        </Categorie>
                        <Categorie icon="⚕️" titre="Santé">
                          {santeListe.length ? santeListe.map((p, i) => (
                            <li key={i}>{p.nom} <span className="text-gray-400">— {(p.distanceM / 1000).toFixed(1)} km</span></li>
                          )) : <li className="text-gray-400">Aucun résultat proche</li>}
                        </Categorie>
                      </div>
                      <div className="mt-4 rounded-lg px-3 py-2.5 text-xs text-gray-700" style={{ backgroundColor: 'rgba(242,125,73,0.08)', border: '1px solid rgba(242,125,73,0.25)' }}>
                        💡 {administrationListe.length + santeListe.length + ecolesListe.length + transportsListe.length} point(s) d'intérêt identifié(s) à proximité (administration, enseignement, transports, santé) — des éléments qui compteront pour de futurs acquéreurs.
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-gray-400">Aucun point d'intérêt trouvé à proximité immédiate.</p>
                  );
                })() : (
                  <p className="text-sm text-gray-400">
                    {environnementLoading ? 'Chargement des données de proximité...' : 'Données de proximité non disponibles pour cette adresse.'}
                  </p>
                )}
              </PB>

              {/* ── Page : Le marché immobilier local ── */}
              <PageBreak />
              <PageHeader title={docTitle} />
              <PB icon="📈" title="Le marché immobilier local">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Logement sur la commune</p>
                {urbanisme?.logementCommune?.status === 'ready' && logementDonnees ? (
                  <>
                    <table className="w-full text-sm border-collapse mb-4">
                      <tbody>
                        <tr className="border-b border-gray-100">
                          <td className="py-1.5 text-gray-600">Maisons</td>
                          <td className="py-1.5 text-right font-semibold text-gray-900">{logementDonnees.maisons.toLocaleString('fr-FR')}</td>
                        </tr>
                        <tr>
                          <td className="py-1.5 text-gray-600">Appartements</td>
                          <td className="py-1.5 text-right font-semibold text-gray-900">{logementDonnees.appartements.toLocaleString('fr-FR')}</td>
                        </tr>
                      </tbody>
                    </table>

                    <p className="text-xs font-semibold text-gray-500 mb-1.5">Nombre de pièces des résidences principales</p>
                    <div className="space-y-1 mb-4">
                      {pieceBars.map(b => {
                        const pct = logementTotal ? Math.round((b.valeur / logementTotal) * 100) : 0;
                        return (
                          <div key={b.label} className="flex items-center gap-2">
                            <span className="w-24 flex-shrink-0 text-xs text-gray-600">{b.label}</span>
                            <div className="flex-1 h-3 rounded-full bg-gray-100 overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: PRINT_BRAND }} />
                            </div>
                            <span className="w-9 flex-shrink-0 text-right text-xs text-gray-500">{pct}%</span>
                          </div>
                        );
                      })}
                    </div>

                    <p className="text-xs font-semibold text-gray-500 mb-1.5">Statut d'occupation</p>
                    <div className="space-y-1">
                      {occupationBars.map(b => {
                        const pct = logementTotal ? Math.round((b.valeur / logementTotal) * 100) : 0;
                        return (
                          <div key={b.label} className="flex items-center gap-2">
                            <span className="w-24 flex-shrink-0 text-xs text-gray-600">{b.label}</span>
                            <div className="flex-1 h-3 rounded-full bg-gray-100 overflow-hidden">
                              <div className="h-full rounded-full bg-gray-400" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="w-9 flex-shrink-0 text-right text-xs text-gray-500">{pct}%</span>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-400 mt-3">Donnée officielle INSEE (recensement de la population, dernier millésime disponible).</p>
                  </>
                ) : (
                  <p className="text-sm text-gray-400">
                    {urbanisme?.logementCommune?.status === 'importing' ? 'Données INSEE en cours de chargement (premier accès à ce jeu de données) — disponibles au prochain export.' : 'Données de logement non disponibles pour cette commune.'}
                  </p>
                )}
              </PB>

              <PB icon="🏗️" title="Constructions neuves sur la commune">
                {urbanisme?.constructionsNeuves?.status === 'ready' && urbanisme.constructionsNeuves.donnees ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-lg border border-gray-100 p-3">
                        <p className="text-xs text-gray-500">Logements individuels commencés</p>
                        <p className="text-xl font-bold text-gray-900">{urbanisme.constructionsNeuves.donnees.log_ind_commences ?? '—'}</p>
                      </div>
                      <div className="rounded-lg border border-gray-100 p-3">
                        <p className="text-xs text-gray-500">Logements collectifs commencés</p>
                        <p className="text-xl font-bold text-gray-900">{urbanisme.constructionsNeuves.donnees.log_col_commences ?? '—'}</p>
                      </div>
                      <div className="rounded-lg border border-gray-100 p-3">
                        <p className="text-xs text-gray-500">Logements individuels autorisés</p>
                        <p className="text-xl font-bold text-gray-900">{urbanisme.constructionsNeuves.donnees.log_ind_autorises ?? '—'}</p>
                      </div>
                      <div className="rounded-lg border border-gray-100 p-3">
                        <p className="text-xs text-gray-500">Logements collectifs autorisés</p>
                        <p className="text-xl font-bold text-gray-900">{urbanisme.constructionsNeuves.donnees.log_col_autorises ?? '—'}</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-3">
                      Donnée officielle SDES (Sit@del2){urbanisme.constructionsNeuves.anneesCouvertes?.length ? ` — années ${urbanisme.constructionsNeuves.anneesCouvertes.slice().sort().join(' et ')}` : ''}.
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-gray-400">
                    {urbanisme?.constructionsNeuves?.status === 'importing' ? 'Données en cours de chargement — disponibles au prochain export.' : 'Données de construction neuve non disponibles pour cette commune.'}
                  </p>
                )}
              </PB>

              <PB icon="📉" title="Les taux d'intérêt (OAT)">
                {oatChart ? (
                  <>
                    <svg viewBox={`0 0 ${OAT_CHART_W} ${OAT_CHART_H}`} className="w-full" style={{ height: 140 }}>
                      <path d={oatChart.paths[0]} fill="none" stroke="#16a34a" strokeWidth="2" />
                      <path d={oatChart.paths[1]} fill="none" stroke={PRINT_BRAND} strokeWidth="2" />
                    </svg>
                    <div className="flex gap-4 mt-2 text-xs text-gray-600">
                      <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 inline-block" style={{ backgroundColor: '#16a34a' }} /> OAT 10 ans</span>
                      <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 inline-block" style={{ backgroundColor: PRINT_BRAND }} /> OAT 5 ans</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">Donnée officielle Banque de France (Webstat), taux constatés récents.</p>
                  </>
                ) : (
                  <p className="text-sm text-gray-400">
                    {urbanisme?.tauxOAT?.status === 'importing' ? 'Taux en cours de chargement — disponibles au prochain export.' : "Taux d'intérêt non disponibles."}
                  </p>
                )}
              </PB>

              {/* ── Page 8 : Notre étude de marché ── */}
              <PageBreak />
              <PageHeader title={docTitle} />
              <PB icon="💶" title="Notre étude de marché">
                {montantAffiche != null ? (
                  <>
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b-2 border-gray-200">
                          <th className="text-left font-semibold text-gray-500 pb-2"> </th>
                          <th className="text-right font-semibold text-gray-500 pb-2">Votre bien</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-gray-100">
                          <td className="py-2 text-gray-700">Valeur du bien ({draft.affichageNetVendeur ? 'net vendeur' : 'prix FAI'})</td>
                          <td className="py-2 text-right font-semibold text-gray-900">{formatEUR(montantAffiche)}</td>
                        </tr>
                        <tr>
                          <td className="py-2 text-gray-700">Prix du bien au m²</td>
                          <td className="py-2 text-right font-semibold text-gray-900">
                            {draft.surfaceHabitable && parseFloat(draft.surfaceHabitable) > 0
                              ? `${formatEUR(Math.round(montantAffiche / parseFloat(draft.surfaceHabitable)))}/m²`
                              : '—'}
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <div
                      className="mt-5 rounded-lg border-2 px-5 py-4 flex items-center justify-between"
                      style={{ borderColor: PRINT_BRAND, backgroundColor: 'rgba(242,125,73,0.06)' }}
                    >
                      <span className="text-sm font-semibold text-gray-700">Valeur totale</span>
                      <span className="text-2xl font-bold" style={{ color: PRINT_BRAND }}>{formatEUR(montantAffiche)}</span>
                    </div>

                    {resultat?.prixBas != null && resultat?.prixHaut != null && (
                      <p className="text-xs text-gray-400 mt-2">
                        Fourchette (comparaison) : {formatEUR(resultat.prixBas)} — {formatEUR(resultat.prixHaut)}
                      </p>
                    )}

                    {analyseIa && (
                      <div className="mt-5 pt-4 border-t border-gray-100">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">En résumé</p>
                        <p className="text-sm text-gray-700 whitespace-pre-line">{analyseIa}</p>
                      </div>
                    )}

                    <p className="text-xs text-gray-400 mt-4">
                      {resultat?.prixM2Base != null && `Prix/m² de base (comparables) : ${formatEUR(resultat.prixM2Base)}/m² · `}
                      {resultat?.indiceQualitePct != null && `Indice de qualité : ${resultat.indiceQualitePct > 0 ? '+' : ''}${resultat.indiceQualitePct}% · `}
                      Méthode(s) : {[draft.methodeComparaisonActive && 'Comparaison DVF', draft.methodeCapitalisationActive && 'Capitalisation'].filter(Boolean).join(' + ') || '—'}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-gray-400">
                    Sélectionne au moins un comparable (ou active la capitalisation locative) pour obtenir une estimation.
                  </p>
                )}
              </PB>

              {/* ── Page 9 : Informations légales ── */}
              <PageBreak />
              <PageHeader title={docTitle} />
              <PB icon="ℹ️" title="Informations légales">
                <p className="text-xs text-gray-500">
                  Cette estimation est établie à partir de données publiques (DVF — Demandes de Valeurs Foncières,
                  data.gouv.fr ; cadastre et urbanisme — IGN / Géoportail de l'Urbanisme ; ADEME pour le DPE) et des
                  caractéristiques du bien renseignées par votre conseiller. Elle constitue une aide à la décision et
                  ne remplace pas une expertise immobilière formelle ni un avis de valeur engageant contractuellement
                  l'agence.
                </p>
                <p className="text-xs text-gray-400 font-semibold mt-3 mb-1">Lexique</p>
                <ul className="text-xs text-gray-500 space-y-1">
                  <li><b>DVF</b> : Demandes de Valeurs Foncières — base publique des ventes immobilières réellement enregistrées.</li>
                  <li><b>DPE</b> : Diagnostic de Performance Énergétique.</li>
                  <li><b>PLU</b> : Plan Local d'Urbanisme — règles de construction applicables à une parcelle.</li>
                  <li><b>Net vendeur / FAI</b> : montant perçu par le vendeur, hors honoraires d'agence / prix affiché incluant les honoraires.</li>
                </ul>
                <p className="print-only text-[10px] text-gray-400 mt-3 pt-2 border-t border-gray-100">
                  Document généré le {generatedAt.toLocaleDateString('fr-FR')} à {generatedAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} — document non contractuel.
                </p>
              </PB>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteId != null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 max-w-sm w-full">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Supprimer ce dossier ?</h3>
            <p className="text-sm text-gray-500 mb-5">Cette action est définitive et ne peut pas être annulée.</p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmDeleteId(null)} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-200">
                Annuler
              </button>
              <button onClick={() => supprimerDossier(confirmDeleteId)} className="flex-1 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-xl hover:bg-red-700">
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
